import { useEffect, useMemo, useState } from "react";
import { supabase } from "./supabaseClient";

// ---------- Marketing Hub views: All Marketing calendar, Weekly Reports, SEO ----------
// Data: marketing.content_items (channel calendars), marketing.weekly_reports, marketing.seo_reports

const card = { background: "#fff", border: "1px solid #dde2de", borderRadius: 12, padding: 18, minWidth: 0 };
const eyebrow = { fontFamily: "'IBM Plex Mono', monospace", fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6b7280" };
const h3 = { fontFamily: "'Oswald', sans-serif", fontSize: 17, textTransform: "uppercase", letterSpacing: "0.03em", margin: 0, color: "#14171a" };

function pad2(n) { return String(n).padStart(2, "0"); }
function iso(y, m, d) { return `${y}-${pad2(m + 1)}-${pad2(d)}`; }
function niceDate(s) {
  if (!s) return "";
  const d = new Date(s.length === 10 ? s + "T12:00:00" : s);
  return isNaN(d) ? s : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
function fmtNum(n) { return n == null || n === "" ? "–" : typeof n === "number" ? n.toLocaleString("en-US") : String(n); }
function safeUrl(u) { return /^https?:\/\//i.test(u || "") ? u : undefined; }

function Pill({ tone = "muted", children }) {
  const tones = {
    muted: { bg: "#eef1ee", fg: "#3a3d40" }, ready: { bg: "#fdedc2", fg: "#8a6b0a" },
    done: { bg: "#e2f3e8", fg: "#1f6e3c" }, flag: { bg: "#fbe6de", fg: "#a53f1f" }, teal: { bg: "#e4f3f4", fg: "#1f6e79" },
  };
  const t = tones[tone] || tones.muted;
  return <span className="jg-mono" style={{ fontSize: 10, padding: "3px 8px", borderRadius: 5, background: t.bg, color: t.fg, textTransform: "uppercase", whiteSpace: "nowrap" }}>{children}</span>;
}

function Delta({ cur, prev, higherIsBetter = true, unit = "%" }) {
  if (typeof cur !== "number" || typeof prev !== "number") return null;
  let diff, label;
  if (unit === "pos") { diff = cur - prev; label = (diff > 0 ? "▼ " : diff < 0 ? "▲ " : "") + Math.abs(diff).toFixed(1); }
  else if (unit === "pt") { diff = cur - prev; label = (diff >= 0 ? "+" : "−") + Math.abs(diff).toFixed(2) + " pt"; }
  else {
    if (prev === 0) return cur ? <span className="jg-mono" style={{ fontSize: 11, color: "#1f6e3c", display: "block" }}>new</span> : null;
    diff = ((cur - prev) / prev) * 100; label = (diff > 0 ? "▲ " : diff < 0 ? "▼ " : "") + Math.abs(diff).toFixed(0) + "%";
  }
  const good = unit === "pos" ? diff < 0 : higherIsBetter ? diff > 0 : diff < 0;
  const color = Math.abs(diff) < 1e-9 ? "#6b7280" : good ? "#1f6e3c" : "#a53f1f";
  return <span className="jg-mono" style={{ fontSize: 11, color, display: "block", marginTop: 2 }}>{label}</span>;
}

function useTable(table, orderCol) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    supabase.from(table).select("*").order(orderCol, { ascending: false }).then(({ data, error }) => {
      if (cancelled) return;
      if (error) { setError(error.message); setRows([]); } else setRows(data || []);
    });
    return () => { cancelled = true; };
  }, [table, orderCol]);
  return { rows, error };
}

// ================= ALL MARKETING CALENDAR =================
// One calendar for the marketing team. Rows live in marketing.content_items:
//   Email Marketing  -> channel "email"
//   Insta / FB Post  -> channel "instagram" (or "facebook"), tag "Post"
//   Insta / FB Story -> channel "instagram" (or "facebook"), tag "Story"
//   TikTok           -> channel "tiktok"
// Untouched auto-generated ideas (source planned / ai-scheduled) are hidden.
export const CAL_TYPES = {
  email: { label: "Email Marketing", color: "#3ea4b1", bodyLabel: "Subject + preview text", linkLabel: "Email draft link (Klaviyo, etc.)" },
  post: { label: "Insta / FB · Post", color: "#e2572b", bodyLabel: "Caption", linkLabel: "Asset / draft link" },
  story: { label: "Insta / FB · Story", color: "#d4a017", bodyLabel: "Story text / idea", linkLabel: "Asset / draft link" },
  tiktok: { label: "TikTok", color: "#14171a", bodyLabel: "Caption / script", linkLabel: "Video / draft link" },
  blog: { label: "Blog", color: "#5b5f97", bodyLabel: "Summary / outline", linkLabel: "Draft or published post link" },
};
const TYPE_ORDER = ["email", "post", "story", "tiktok", "blog"];
function useIsMobile(bp = 700) {
  const get = () => typeof window !== "undefined" && window.innerWidth < bp;
  const [m, setM] = useState(get);
  useEffect(() => { const on = () => setM(get()); window.addEventListener("resize", on); return () => window.removeEventListener("resize", on); });
  return m;
}
function typeOf(row) {
  if (row.channel === "email") return "email";
  if (row.channel === "tiktok") return "tiktok";
  if (row.channel === "blog") return "blog";
  return row.tag === "Story" ? "story" : "post";
}
function statusOf(it) { return it.done ? "Posted" : it.created_flag ? "Created" : "Planned"; }

function stamp(isoStr) {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  return isNaN(d) ? "" : d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
function Byline({ it, compact }) {
  const parts = [];
  if (it.done && it.posted_by) parts.push(`Posted by ${it.posted_by}${it.posted_at ? " · " + stamp(it.posted_at) : ""}`);
  if (it.edited_by) parts.push(`${compact ? "" : "Updated by "}${it.edited_by}${it.edited_at ? " · " + stamp(it.edited_at) : ""}`);
  else if (it.updated_at) parts.push(`Updated ${stamp(it.updated_at)}`);
  if (!parts.length) return null;
  if (compact) return <div className="jg-mono" style={{ fontSize: 10, color: "#6b7280", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{parts[parts.length - 1]}</div>;
  return <div className="jg-mono" style={{ fontSize: 10.5, color: "#6b7280", marginTop: 8, paddingTop: 6, borderTop: "1px dashed #e3e7e4", display: "flex", flexDirection: "column", gap: 2 }}>{parts.map((p, i) => <span key={i}>{p}</span>)}</div>;
}
function parseIso(s) { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); }
function toIso(dt) { return iso(dt.getFullYear(), dt.getMonth(), dt.getDate()); }
function addDays(s, n) { const d = parseIso(s); d.setDate(d.getDate() + n); return toIso(d); }
function weekStart(s) { const d = parseIso(s); d.setDate(d.getDate() - d.getDay()); return toIso(d); }

export function AllMarketingCalendar({ assignedChannels = [], isAdmin, userName, userId }) {
  const today = new Date();
  const todayIso = toIso(today);
  const [mode, setMode] = useState("week"); // day | week | month — opens on week
  const [anchor, setAnchor] = useState(todayIso);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [hidden, setHiddenState] = useState(() => { try { return JSON.parse(localStorage.getItem("jg-cal-hidden") || "{}"); } catch (e) { return {}; } });
  const setHidden = (fn) => setHiddenState((h) => { const next = typeof fn === "function" ? fn(h) : fn; try { localStorage.setItem("jg-cal-hidden", JSON.stringify(next)); } catch (e) {} return next; });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [reload, setReload] = useState(0);
  const isMobile = useIsMobile();
  useEffect(() => {
    if (!userId) return;
    supabase.from("notification_settings").select("cal_view,cal_hidden").eq("user_id", userId).maybeSingle().then(({ data }) => {
      if (!data) return;
      if (data.cal_view) setMode(data.cal_view);
      if (data.cal_hidden) setHiddenState(data.cal_hidden);
    });
  }, [userId]);

  const canSocial = isAdmin || assignedChannels.includes("instagram") || assignedChannels.includes("facebook");
  const allowed = {
    email: isAdmin || assignedChannels.includes("email"),
    post: canSocial, story: canSocial,
    tiktok: !!isAdmin,
    blog: isAdmin || assignedChannels.includes("blog"),
  };
  const allowedTypes = TYPE_ORDER.filter((t) => allowed[t]);
  const canEdit = (it) => allowed[it._type];
  const me = userName || "Someone";

  // visible range
  const a = parseIso(anchor);
  let start, end;
  if (mode === "day") { start = end = anchor; }
  else if (mode === "week") { start = weekStart(anchor); end = addDays(start, 6); }
  else { start = iso(a.getFullYear(), a.getMonth(), 1); end = iso(a.getFullYear(), a.getMonth(), new Date(a.getFullYear(), a.getMonth() + 1, 0).getDate()); }

  useEffect(() => {
    let cancelled = false;
    setRows(null); setError(null);
    supabase.from("content_items").select("*").gte("item_date", start).lte("item_date", end).order("item_date").then(({ data, error }) => {
      if (cancelled) return;
      if (error) { setError(error.message); setRows([]); return; }
      setRows((data || []).filter((r) => r.source === "manual" || r.created_flag || r.done || !["planned", "ai-scheduled"].includes(r.source)));
    });
    return () => { cancelled = true; };
  }, [start, end, reload]);

  const byDate = useMemo(() => {
    const map = {};
    (rows || []).forEach((r) => { const t = typeOf(r); if (hidden[t]) return; (map[r.item_date] = map[r.item_date] || []).push({ ...r, _type: t }); });
    return map;
  }, [rows, hidden]);

  function move(dir) {
    setSelected(null);
    if (mode === "day") setAnchor(addDays(anchor, dir));
    else if (mode === "week") setAnchor(addDays(anchor, 7 * dir));
    else { const d = new Date(a.getFullYear(), a.getMonth() + dir, 1); setAnchor(toIso(d)); }
  }
  const label = mode === "day"
    ? a.toLocaleDateString("en-US", { weekday: isMobile ? "short" : "long", month: "short", day: "numeric", year: "numeric" })
    : mode === "week"
      ? `${parseIso(start).toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${parseIso(end).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
      : a.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  function openNew(date) {
    if (!allowedTypes.length) return;
    setConfirmDelete(false);
    setForm({ type: allowedTypes[0], date: date || (mode === "day" ? anchor : todayIso), title: "", body: "", notes: "", link: "", files: [], repeat: "none", repeatUntil: "" });
  }
  function openEdit(it) {
    setConfirmDelete(false);
    setForm({ id: it.id, item: it, type: it._type, channel: it.channel, date: it.item_date, title: it.title || "", body: it.body || "", notes: (it.steps || []).filter((x) => x !== "—").join("\n"), link: it.link || "", files: Array.isArray(it.files) ? it.files : [] });
  }
  const horizonFrom = (d) => addDays(d > todayIso ? d : todayIso, 365);
  // Keep "forever" repeats filled 12 months ahead
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const soon = addDays(todayIso, 300);
      const { data, error } = await supabase.from("repeat_series").select("*").eq("active", true).lt("last_date", soon);
      if (error || !data || !data.length || cancelled) return;
      let added = false;
      for (const sr of data) {
        const horizon = horizonFrom(sr.first_date);
        const dates = repeatDates(sr.first_date, sr.every, horizon, 2000).filter((d) => d > sr.last_date);
        if (!dates.length) continue;
        const claim = await supabase.from("repeat_series").update({ last_date: dates[dates.length - 1] }).eq("id", sr.id).eq("last_date", sr.last_date).select("id");
        if (claim.error || !claim.data || !claim.data.length) continue;
        const ins = await supabase.from("content_items").insert(dates.map((d) => ({ ...sr.template, item_date: d, source: "manual", created_flag: false, done: false, repeat_group: sr.id })));
        if (ins.error) await supabase.from("repeat_series").update({ last_date: sr.last_date }).eq("id", sr.id);
        else added = true;
      }
      if (added && !cancelled) setReload((n) => n + 1);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [uploading, setUploading] = useState(false);
  async function uploadFiles(fileList) {
    const picked = Array.from(fileList || []);
    if (!picked.length) return;
    setUploading(true); setError(null);
    const added = [];
    for (const f of picked) {
      if (f.size > 50 * 1024 * 1024) { setError(`${f.name} is over 50 MB.`); continue; }
      const safe = f.name.replace(/[^A-Za-z0-9._-]+/g, "-");
      const path = `${new Date().toISOString().slice(0, 10)}/${Math.random().toString(36).slice(2, 10)}-${safe}`;
      const { error } = await supabase.storage.from("marketing-files").upload(path, f, { contentType: f.type || undefined });
      if (error) { setError(/bucket|not found/i.test(error.message) ? "File uploads aren't set up yet. Run supabase-repeat-files.sql in Supabase." : `Couldn't upload ${f.name}: ${error.message}`); continue; }
      const { data } = supabase.storage.from("marketing-files").getPublicUrl(path);
      added.push({ name: f.name, path, url: data.publicUrl, size: f.size, type: f.type || "" });
    }
    setUploading(false);
    if (added.length) setForm((fm) => (fm ? { ...fm, files: [...(fm.files || []), ...added] } : fm));
  }
  function repeatDates(first, every, until, bigCap) {
    if (every === "none") return [first];
    const caps = bigCap ? { day: bigCap, week: bigCap, month: bigCap } : { day: 120, week: 104, month: 36 };
    const out = [first];
    const base = parseIso(first);
    for (let n = 1; n < caps[every]; n++) {
      let d;
      if (every === "day") { d = new Date(base); d.setDate(base.getDate() + n); }
      else if (every === "week") { d = new Date(base); d.setDate(base.getDate() + 7 * n); }
      else { const last = new Date(base.getFullYear(), base.getMonth() + n + 1, 0).getDate(); d = new Date(base.getFullYear(), base.getMonth() + n, Math.min(base.getDate(), last)); }
      const di = toIso(d);
      if (di > until) break;
      out.push(di);
    }
    return out;
  }
  async function save() {
    if (!form.title.trim() || !form.date) { setError("Add a title and a date before saving."); return; }
    if (!form.id && form.repeat !== "none" && !form.forever && (!form.repeatUntil || form.repeatUntil < form.date)) { setError("Pick a repeat-until date on or after the first date."); return; }
    setSaving(true); setError(null);
    const channel = form.type === "email" ? "email" : form.type === "tiktok" ? "tiktok" : form.type === "blog" ? "blog"
      : (form.channel === "instagram" || form.channel === "facebook") ? form.channel
      : (isAdmin || assignedChannels.includes("instagram")) ? "instagram" : "facebook";
    const now = new Date().toISOString();
    const row = {
      channel, item_date: form.date, title: form.title.trim(), body: form.body,
      tag: form.type === "story" ? "Story" : form.type === "post" ? "Post" : null,
      type: CAL_TYPES[form.type].label,
      steps: form.notes.split("\n").map((s) => s.trim()).filter(Boolean),
      link: form.link.trim(), files: form.files || [],
      updated_at: now, edited_by: me, edited_at: now,
    };
    let res;
    if (form.id) {
      res = await supabase.from("content_items").update(row).eq("id", form.id);
    } else {
      const forever = form.repeat !== "none" && form.forever;
      const dates = forever ? repeatDates(form.date, form.repeat, horizonFrom(form.date), 2000) : repeatDates(form.date, form.repeat, form.repeatUntil);
      const group = (dates.length > 1 || forever) && window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : null;
      if (forever) {
        if (!group) { setSaving(false); setError("This browser can't create a repeating series. Try Chrome or Safari."); return; }
        const sr = await supabase.from("repeat_series").insert({ id: group, every: form.repeat, first_date: form.date, last_date: dates[dates.length - 1], template: row });
        if (sr.error) { setSaving(false); setError(/repeat_series|schema cache|does not exist/i.test(sr.error.message) ? "Repeat forever isn't set up yet. Run supabase-repeat-forever.sql in Supabase." : `Couldn't save: ${sr.error.message}`); return; }
      }
      res = await supabase.from("content_items").insert(dates.map((d) => ({ ...row, item_date: d, source: "manual", created_flag: false, done: false, ...(group ? { repeat_group: group } : {}) })));
    }
    setSaving(false);
    if (res.error) { setError(/files|repeat_group/i.test(res.error.message) ? "Repeats and file uploads aren't set up yet. Run supabase-repeat-files.sql in Supabase." : `Couldn't save: ${res.error.message}`); return; }
    const date = form.date;
    setForm(null);
    if (date < start || date > end) { setAnchor(date); } else setReload((n) => n + 1);
    if (mode === "month") setSelected(date);
  }
  async function remove(allLater) {
    setSaving(true);
    const g = form.item && form.item.repeat_group;
    const { error } = allLater && g
      ? await supabase.from("content_items").delete().eq("repeat_group", g).gte("item_date", form.item.item_date)
      : await supabase.from("content_items").delete().eq("id", form.id);
    if (!error && allLater && g) await supabase.from("repeat_series").update({ active: false }).eq("id", g);
    setSaving(false);
    if (error) { setError(`Couldn't delete: ${error.message}`); return; }
    setForm(null); setReload((n) => n + 1);
  }
  const [dragId, setDragId] = useState(null);
  const [dropDay, setDropDay] = useState(null);
  async function moveItem(id, toDay) {
    const it = (rows || []).find((r) => r.id === id);
    setDragId(null); setDropDay(null);
    if (!it || it.item_date === toDay) return;
    const now = new Date().toISOString();
    const prev = rows;
    setRows(rows.map((r) => (r.id === id ? { ...r, item_date: toDay, edited_by: me, edited_at: now } : r)));
    const { error } = await supabase.from("content_items").update({ item_date: toDay, updated_at: now, edited_by: me, edited_at: now }).eq("id", id);
    if (error) { setRows(prev); setError(`Couldn't move it: ${error.message}`); }
  }
  const dropProps = (day) => isMobile ? {} : {
    onDragOver: (e) => { if (dragId) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dropDay !== day) setDropDay(day); } },
    onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget) && dropDay === day) setDropDay(null); },
    onDrop: (e) => { e.preventDefault(); const id = e.dataTransfer.getData("text/plain") || dragId; if (id) moveItem(id, day); },
  };
  const dragProps = (it) => (isMobile || !canEdit(it)) ? {} : {
    draggable: true,
    onDragStart: (e) => { e.dataTransfer.setData("text/plain", it.id); e.dataTransfer.effectAllowed = "move"; setDragId(it.id); },
    onDragEnd: () => { setDragId(null); setDropDay(null); },
  };
  async function markPosted(it) {
    const now = new Date().toISOString();
    const patch = it.done
      ? { done: false, posted_by: null, posted_at: null, updated_at: now, edited_by: me, edited_at: now }
      : { done: true, created_flag: true, posted_by: me, posted_at: now, updated_at: now, edited_by: me, edited_at: now };
    const { error } = await supabase.from("content_items").update(patch).eq("id", it.id);
    if (error) setError(`Couldn't update: ${error.message}`); else setReload((n) => n + 1);
  }

  const ItemDetail = ({ it }) => (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "12px 0", borderTop: "1px solid #eef1ee" }}>
      <span style={{ width: 10, height: 10, borderRadius: 3, background: CAL_TYPES[it._type].color, marginTop: 4, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#14171a", textDecoration: it.done ? "line-through" : "none" }}>{it.title}</div>
        {it.body && <div style={{ fontSize: 13, color: "#3a3d40", marginTop: 3, whiteSpace: "pre-wrap" }}>{it.body}</div>}
        {(it.steps || []).filter((x) => x !== "—").length > 0 && <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 12.5, color: "#6b7280" }}>{it.steps.filter((x) => x !== "—").map((s, i) => <li key={i}>{s}</li>)}</ul>}
        <div style={{ display: "flex", gap: 8, marginTop: 7, flexWrap: "wrap", alignItems: "center" }}>
          <Pill tone="teal">{CAL_TYPES[it._type].label}</Pill>
          <Pill tone={it.done ? "done" : it.created_flag ? "ready" : "muted"}>{statusOf(it)}</Pill>
          {safeUrl(it.link) && <a href={it.link} target="_blank" rel="noreferrer" style={{ fontSize: 12.5, color: "#1f6e79" }}>Open link →</a>}
          {(Array.isArray(it.files) ? it.files : []).map((f, x) => <a key={x} href={f.url} target="_blank" rel="noreferrer" style={{ fontSize: 12.5, color: "#1f6e79" }}>📎 {f.name}</a>)}
          {canEdit(it) && <button onClick={() => markPosted(it)} style={linkBtn}>{it.done ? "Mark not posted" : "Mark posted"}</button>}
          {canEdit(it) && <button onClick={() => openEdit(it)} style={linkBtn}>Edit</button>}
        </div>
        <Byline it={it} />
      </div>
    </div>
  );

  const weekDays = mode === "week" ? Array.from({ length: 7 }, (_, i) => addDays(start, i)) : [];
  const selItems = selected ? byDate[selected] || [] : [];

  // month grid cells
  const monthCells = [];
  if (mode === "month") {
    const first = new Date(a.getFullYear(), a.getMonth(), 1).getDay();
    const total = new Date(a.getFullYear(), a.getMonth() + 1, 0).getDate();
    for (let i = 0; i < first; i++) monthCells.push(null);
    for (let d = 1; d <= total; d++) monthCells.push(iso(a.getFullYear(), a.getMonth(), d));
    while (monthCells.length % 7) monthCells.push(null);
  }

  return (
    <>
      <div className="jg-mono" style={{ fontSize: 11, color: "#3ea4b1", letterSpacing: "0.08em", marginBottom: 4 }}>MARKETING CALENDAR</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
        <h1 className="jg-display" style={{ margin: 0, fontSize: 30, color: "#14171a", textTransform: "uppercase" }}>Calendar</h1>
        {allowedTypes.length > 0 && <button onClick={() => openNew(selected)} style={primaryBtn}>+ Content</button>}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
        <div role="tablist" aria-label="Calendar view" style={{ display: "inline-flex", background: "#fff", border: "1px solid #dde2de", borderRadius: 9, padding: 3 }}>
          {[["day", "Daily"], ["week", "Weekly"], ["month", "Monthly"]].map(([id, lab]) => (
            <button key={id} role="tab" aria-selected={mode === id} onClick={() => { setMode(id); setSelected(null); }}
              style={{ border: "none", borderRadius: 7, padding: "7px 14px", fontSize: 13, fontWeight: 600, background: mode === id ? "#14171a" : "transparent", color: mode === id ? "#fff" : "#3a3d40" }}>{lab}</button>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={() => move(-1)} style={navBtn} aria-label="Previous">‹</button>
          <span className="jg-display" style={{ fontSize: isMobile ? 15 : 17, minWidth: isMobile ? 0 : 200, textAlign: "center", textTransform: "uppercase" }}>{label}</span>
          <button onClick={() => move(1)} style={navBtn} aria-label="Next">›</button>
          <button onClick={() => { setAnchor(todayIso); setSelected(null); }} style={{ ...navBtn, width: "auto", padding: "0 12px", fontSize: 13 }}>Today</button>
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <span className="jg-mono" style={{ fontSize: 10.5, color: "#6b7280", letterSpacing: "0.08em", marginRight: 2 }}>SHOW</span>
        {TYPE_ORDER.map((t) => {
          const on = !hidden[t];
          return (
            <button key={t} onClick={() => setHidden((h) => ({ ...h, [t]: !h[t] }))} aria-pressed={on}
              style={{ display: "inline-flex", alignItems: "center", gap: 7, border: on ? `1.5px solid ${CAL_TYPES[t].color}` : "1.5px dashed #c7cdc9", background: on ? CAL_TYPES[t].color + "14" : "transparent", color: on ? "#14171a" : "#9aa1a5", borderRadius: 999, padding: "5px 12px 5px 7px", fontSize: 12.5, fontWeight: on ? 600 : 400 }}>
              <span style={{ width: 16, height: 16, borderRadius: 4, display: "inline-flex", alignItems: "center", justifyContent: "center", background: on ? CAL_TYPES[t].color : "#fff", border: on ? "none" : "1.5px solid #c7cdc9", color: "#fff", fontSize: 11, lineHeight: 1 }}>{on ? "✓" : ""}</span>
              {CAL_TYPES[t].label}
            </button>
          );
        })}
        <span style={{ display: "inline-flex", gap: 10, marginLeft: 4 }}>
          <button onClick={() => setHidden({})} style={linkBtn}>Show all</button>
          <button onClick={() => setHidden(Object.fromEntries(TYPE_ORDER.map((t) => [t, true])))} style={{ ...linkBtn, color: "#6b7280" }}>Hide all</button>
        </span>
      </div>

      {error && <div style={{ ...card, color: "#a53f1f", marginBottom: 12, fontSize: 13 }}>{error}</div>}
      {rows === null && <p style={{ color: "#6b7280", fontSize: 13 }}>Loading…</p>}

      {/* WEEKLY */}
      {mode === "week" && rows && (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(7, minmax(0, 1fr))", gap: isMobile ? 8 : 8 }}>
          {weekDays.map((day) => {
            const list = byDate[day] || [];
            const d = parseIso(day);
            const isToday = day === todayIso;
            return (
              <div key={day} {...dropProps(day)} onClick={() => setSelected(selected === day ? null : day)} style={{ cursor: "pointer", background: dropDay === day ? "#e4f3f4" : selected === day ? "#f3fafb" : "#fff", outline: dropDay === day ? "2px dashed #3ea4b1" : "none", outlineOffset: -2, border: selected === day ? "2px solid #3ea4b1" : isToday ? "2px solid #9fd3d9" : "1px solid #dde2de", borderRadius: 10, padding: 8, minHeight: isMobile ? 0 : 220, display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                  <button onClick={(e) => { e.stopPropagation(); setSelected(selected === day ? null : day); }} style={{ background: "none", border: "none", padding: 0, textAlign: "left", display: "flex", alignItems: "baseline", gap: 6 }} title="Show this day below">
                    <span className="jg-mono" style={{ fontSize: 10.5, color: "#6b7280" }}>{d.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase()}</span>
                    <span className="jg-display" style={{ fontSize: 18, color: isToday ? "#1f6e79" : "#14171a" }}>{d.getDate()}</span>
                  </button>
                  {allowedTypes.length > 0 && <button onClick={(e) => { e.stopPropagation(); openNew(day); }} aria-label={`Add content on ${day}`} style={{ border: "none", background: "#eef1ee", color: "#3a3d40", borderRadius: 6, width: 24, height: 24, fontSize: 15, lineHeight: 1 }}>+</button>}
                </div>
                {list.length === 0 && <span style={{ fontSize: 11.5, color: "#b4babd" }}>Nothing scheduled</span>}
                {list.map((it) => (
                  <button key={it.id} {...dragProps(it)} onClick={(e) => { e.stopPropagation(); setSelected(day); }}
                    style={{ cursor: canEdit(it) && !isMobile ? "grab" : "pointer", textAlign: "left", background: CAL_TYPES[it._type].color + "14", border: "none", borderLeft: `3px solid ${CAL_TYPES[it._type].color}`, borderRadius: 6, padding: "6px 7px", minWidth: 0, opacity: dragId === it.id ? 0.35 : it.done ? 0.65 : 1 }}>
                    <div className="jg-mono" style={{ fontSize: 9.5, color: "#6b7280", textTransform: "uppercase" }}>{CAL_TYPES[it._type].label} · {statusOf(it)}</div>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: "#14171a", lineHeight: 1.3, marginTop: 2, wordBreak: "break-word", textDecoration: it.done ? "line-through" : "none" }}>{it.title}</div>
                    {Array.isArray(it.files) && it.files.length > 0 && <div className="jg-mono" style={{ fontSize: 10, color: "#3a3d40", marginTop: 3 }}>📎 {it.files.length} file{it.files.length === 1 ? "" : "s"}</div>}
                    <Byline it={it} compact />
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* DAILY */}
      {mode === "day" && rows && (
        <div style={card}>
          <div style={rowBetween}>
            <h3 style={h3}>{a.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</h3>
            {allowedTypes.length > 0 && <button onClick={() => openNew(anchor)} style={linkBtn}>+ Content on this day</button>}
          </div>
          {(byDate[anchor] || []).length === 0 && <p style={{ margin: "10px 0 0", color: "#6b7280", fontSize: 13 }}>Nothing scheduled.</p>}
          {(byDate[anchor] || []).map((it) => <ItemDetail key={it.id} it={it} />)}
        </div>
      )}

      {/* MONTHLY */}
      {mode === "month" && rows && (
        <>
          <div style={{ overflowX: "auto" }}>
            <div style={{ minWidth: isMobile ? 0 : 700, display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: isMobile ? 4 : 6 }}>
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="jg-mono" style={{ fontSize: 10, color: "#6b7280", textAlign: "center", padding: "4px 0" }}>{isMobile ? d[0] : d.toUpperCase()}</div>)}
              {monthCells.map((key, i) => {
                if (!key) return <div key={i} />;
                const d = parseIso(key).getDate();
                const list = byDate[key] || [];
                const isSel = selected === key;
                if (isMobile) return (
                  <button key={i} onClick={() => setSelected(isSel ? null : key)} aria-label={`${d}: ${list.length} items`}
                    style={{ background: isSel ? "#e4f3f4" : "#fff", minHeight: 52, borderRadius: 8, padding: "5px 2px", border: isSel ? "2px solid #3ea4b1" : key === todayIso ? "2px solid #9fd3d9" : "1px solid #dde2de", display: "flex", flexDirection: "column", alignItems: "center", gap: 4, minWidth: 0 }}>
                    <span className="jg-mono" style={{ fontSize: 12, color: key === todayIso ? "#1f6e79" : "#14171a" }}>{d}</span>
                    <span style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 2, maxWidth: "100%" }}>
                      {list.slice(0, 6).map((it) => <span key={it.id} style={{ width: 6, height: 6, borderRadius: 2, background: CAL_TYPES[it._type].color, opacity: it.done ? 0.45 : 1 }} />)}
                    </span>
                  </button>
                );
                return (
                  <div key={i} {...dropProps(key)} onClick={() => setSelected(isSel ? null : key)}
                    style={{ background: dropDay === key ? "#e4f3f4" : "#fff", outline: dropDay === key ? "2px dashed #3ea4b1" : "none", outlineOffset: -2, minHeight: 100, borderRadius: 8, padding: 6, cursor: "pointer", border: isSel ? "2px solid #3ea4b1" : key === todayIso ? "2px solid #9fd3d9" : "1px solid #dde2de", display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span className="jg-mono" style={{ fontSize: 11, color: key === todayIso ? "#1f6e79" : "#6b7280" }}>{d}</span>
                      {allowedTypes.length > 0 && <button onClick={(e) => { e.stopPropagation(); openNew(key); }} aria-label={`Add content on ${key}`} title="Add content" style={{ border: "none", background: "#eef1ee", color: "#3a3d40", borderRadius: 5, width: 20, height: 20, fontSize: 13, lineHeight: 1, padding: 0 }}>+</button>}
                    </div>
                    {list.slice(0, 4).map((it) => (
                      <span key={it.id} {...dragProps(it)} onClick={(e) => e.stopPropagation() || setSelected(key)} title={`${CAL_TYPES[it._type].label}: ${it.title}${canEdit(it) ? " (drag to move)" : ""}`} style={{ cursor: canEdit(it) ? "grab" : "pointer", fontSize: 10.5, lineHeight: 1.25, padding: "2px 5px", borderRadius: 4, background: CAL_TYPES[it._type].color + "22", borderLeft: `3px solid ${CAL_TYPES[it._type].color}`, color: "#14171a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", opacity: dragId === it.id ? 0.35 : it.done ? 0.6 : 1, textDecoration: it.done ? "line-through" : "none" }}>{it.title}</span>
                    ))}
                    {list.length > 4 && <span className="jg-mono" style={{ fontSize: 10, color: "#6b7280" }}>+{list.length - 4} more</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {(mode === "month" || mode === "week") && rows && selected && (
            <div style={{ ...card, marginTop: 16 }}>
              <div style={rowBetween}>
                <h3 style={h3}>{parseIso(selected).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</h3>
                <div style={{ display: "flex", gap: 10 }}>
                  {allowedTypes.length > 0 && <button onClick={() => openNew(selected)} style={linkBtn}>+ Content on this day</button>}
                  <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", color: "#6b7280", fontSize: 13 }}>Close</button>
                </div>
              </div>
              {selItems.length === 0 && <p style={{ margin: "10px 0 0", color: "#6b7280", fontSize: 13 }}>Nothing scheduled.</p>}
              {selItems.map((it) => <ItemDetail key={it.id} it={it} />)}
            </div>
          )}

      {form && (
        <div onClick={() => !saving && setForm(null)} style={{ position: "fixed", inset: 0, background: "rgba(20,23,26,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 50 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, padding: 24, width: "100%", maxWidth: 520, maxHeight: "92vh", overflowY: "auto", boxSizing: "border-box" }}>
            <h2 className="jg-display" style={{ margin: "0 0 16px", fontSize: 22, textTransform: "uppercase" }}>{form.id ? "Edit content" : "Add content"}</h2>
            <label style={lbl}>Type</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
              {allowedTypes.map((t) => (
                <button key={t} type="button" onClick={() => setForm({ ...form, type: t })}
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderRadius: 8, fontSize: 13, textAlign: "left", border: form.type === t ? `2px solid ${CAL_TYPES[t].color}` : "1px solid #dde2de", background: form.type === t ? CAL_TYPES[t].color + "14" : "#fff", color: "#14171a" }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: CAL_TYPES[t].color }} />{CAL_TYPES[t].label}
                </button>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div><label style={lbl} htmlFor="cal-date">Date</label><input id="cal-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} style={inp} /></div>
              {!form.id && <div><label style={lbl} htmlFor="cal-repeat">Repeat</label>
                <select id="cal-repeat" value={form.repeat} onChange={(e) => {
                  const v = e.target.value; const d = parseIso(form.date || todayIso);
                  const until = v === "day" ? addDays(form.date, 29) : v === "week" ? toIso(new Date(d.getFullYear(), d.getMonth() + 3, d.getDate())) : v === "month" ? toIso(new Date(d.getFullYear() + 1, d.getMonth(), d.getDate())) : "";
                  setForm({ ...form, repeat: v, repeatUntil: v === "none" ? "" : (form.repeatUntil && form.repeat === v ? form.repeatUntil : until) });
                }} style={inp}>
                  <option value="none">No repeat</option><option value="day">Every day</option><option value="week">Every week</option><option value="month">Every month</option>
                </select></div>}
            </div>
            {!form.id && form.repeat !== "none" && (() => {
              const n = form.repeatUntil && form.repeatUntil >= form.date ? repeatDates(form.date, form.repeat, form.repeatUntil).length : 0;
              return (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, alignItems: "end" }}>
                  <div><label style={lbl} htmlFor="cal-until">Repeat until</label><input id="cal-until" type="date" min={form.date} value={form.forever ? "" : form.repeatUntil} disabled={form.forever} onChange={(e) => setForm({ ...form, repeatUntil: e.target.value })} style={{ ...inp, opacity: form.forever ? 0.45 : 1 }} /></div>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, paddingBottom: 10, cursor: "pointer" }}>
                    <input type="checkbox" checked={!!form.forever} onChange={(e) => setForm({ ...form, forever: e.target.checked })} style={{ width: 16, height: 16, accentColor: "#3ea4b1" }} /> Forever
                  </label>
                  <div style={{ gridColumn: "1 / -1", fontSize: 12.5, color: "#6b7280", marginTop: -4 }}>{form.forever ? "Keeps repeating with no end date. The calendar always stays filled 12 months ahead." : n ? `Adds ${n} item${n === 1 ? "" : "s"} to the calendar` : "Pick an end date, or check Forever"}</div>
                </div>
              );
            })()}
            <label style={lbl} htmlFor="cal-title">Title</label>
            <input id="cal-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Fall hoodie drop" style={inp} />
            <label style={lbl} htmlFor="cal-body">{CAL_TYPES[form.type].bodyLabel}</label>
            <textarea id="cal-body" rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} style={{ ...inp, resize: "vertical" }} />
            <label style={lbl} htmlFor="cal-notes">Prep notes <span style={{ color: "#9aa1a5" }}>(one per line, optional)</span></label>
            <textarea id="cal-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder={"Shoot photos of the new hoodies\nGet logo approval"} style={{ ...inp, resize: "vertical" }} />
            <label style={lbl} htmlFor="cal-link">{CAL_TYPES[form.type].linkLabel} <span style={{ color: "#9aa1a5" }}>(optional)</span></label>
            <input id="cal-link" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://" style={inp} />
            <label style={lbl} htmlFor="cal-files">Files <span style={{ color: "#9aa1a5" }}>(optional · images, video, PDF, docs)</span></label>
            <label htmlFor="cal-files" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, border: "1.5px dashed #c7cdc9", borderRadius: 8, padding: "14px 12px", fontSize: 13, color: "#3a3d40", cursor: "pointer", background: "#fafbfa" }}
              onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); uploadFiles(e.dataTransfer.files); }}>
              {uploading ? "Uploading…" : <><b style={{ color: "#1f6e79" }}>Choose files</b> or drag them here</>}
            </label>
            <input id="cal-files" type="file" multiple onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} style={{ display: "none" }} />
            {(form.files || []).length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
                {form.files.map((f, idx) => (
                  <div key={f.path || idx} style={{ display: "flex", alignItems: "center", gap: 8, border: "1px solid #dde2de", borderRadius: 8, padding: "6px 8px" }}>
                    {/^image\//.test(f.type) ? <img src={f.url} alt="" style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 5 }} /> : <span className="jg-mono" style={{ width: 36, height: 36, borderRadius: 5, background: "#eef1ee", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9.5 }}>{(f.name.split(".").pop() || "FILE").slice(0, 4).toUpperCase()}</span>}
                    <a href={f.url} target="_blank" rel="noreferrer" style={{ flex: 1, minWidth: 0, fontSize: 13, color: "#1f6e79", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</a>
                    <button type="button" onClick={() => setForm({ ...form, files: form.files.filter((_, x) => x !== idx) })} aria-label={`Remove ${f.name}`} style={{ background: "none", border: "none", color: "#a53f1f", fontSize: 16, lineHeight: 1 }}>×</button>
                  </div>
                ))}
              </div>
            )}
            {form.item && <Byline it={form.item} />}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 18, flexWrap: "wrap" }}>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={save} disabled={saving} style={primaryBtn}>{saving ? "Saving…" : form.id ? "Save changes" : "Add to calendar"}</button>
                <button onClick={() => setForm(null)} disabled={saving} style={{ background: "#eef1ee", color: "#3a3d40", border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 13 }}>Cancel</button>
              </div>
              {form.id && (confirmDelete
                ? <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <button onClick={() => remove(false)} disabled={saving} style={{ background: "#a53f1f", color: "#fff", border: "none", borderRadius: 8, padding: "10px 12px", fontSize: 13, fontWeight: 600 }}>{form.item && form.item.repeat_group ? "Delete this one" : "Confirm delete"}</button>
                    {form.item && form.item.repeat_group && <button onClick={() => remove(true)} disabled={saving} style={{ background: "#fff", color: "#a53f1f", border: "1.5px solid #a53f1f", borderRadius: 8, padding: "9px 12px", fontSize: 13, fontWeight: 600 }}>Delete this + all later repeats</button>}
                  </div>
                : <button onClick={() => setConfirmDelete(true)} style={{ background: "none", border: "none", color: "#a53f1f", fontSize: 13 }}>Delete</button>)}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
const navBtn = { width: 34, height: 34, borderRadius: 8, border: "1px solid #dde2de", background: "#fff", fontSize: 18, color: "#14171a" };
const linkBtn = { background: "none", border: "none", padding: 0, color: "#1f6e79", fontSize: 12.5, fontWeight: 600 };
const primaryBtn = { background: "#3ea4b1", color: "#fff", border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 13, fontWeight: 600 };
const lbl = { display: "block", fontSize: 12, color: "#3a3d40", fontWeight: 600, margin: "10px 0 5px" };
const inp = { width: "100%", padding: "9px 10px", border: "1px solid #dde2de", borderRadius: 8, fontSize: 13, fontFamily: "inherit", background: "#fff", color: "#14171a" };

// ================= WEEKLY REPORTS =================
export function WeeklyReports() {
  const { rows, error } = useTable("weekly_reports", "week_of");
  const [sel, setSel] = useState(null);
  if (rows === null) return <Header title="Weekly Reports" eyebrowText="GOOGLE · YELP · CLARITY · ANALYTICS"><p style={{ color: "#6b7280" }}>Loading…</p></Header>;
  if (!rows.length) return (
    <Header title="Weekly Reports" eyebrowText="GOOGLE · YELP · CLARITY · ANALYTICS">
      {error && <div style={{ color: "#a53f1f", fontSize: 13, marginBottom: 10 }}>{error}</div>}
      <div style={{ ...card, textAlign: "center", color: "#6b7280" }}>The first weekly report arrives Tuesday morning. Each week shows the Google post, photos, Yelp replies, the Clarity and Google Analytics reports, and what was done where.</div>
    </Header>
  );
  const row = rows.find((r) => r.week_of === sel) || rows[0];
  const w = row.data || {}; const g = w.google || {}; const y = w.yelp || {}; const c = w.clarity || {}; const a = w.analytics || {};
  const posted = row.status === "posted";
  const fmax = Math.max(1, ...(c.funnel || []).map((f) => f.sessions || 0));
  const gmax = Math.max(1, ...(a.channels || []).map((x) => x.sessions || 0));
  return (
    <Header title={"Week of " + niceDate(row.week_of)} eyebrowText="WEEKLY REPORT"
      right={<div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <Pill tone={posted ? "done" : row.status === "partial" ? "flag" : "ready"}>{posted ? "Posted" : row.status === "partial" ? "Partly posted" : "Awaiting start"}</Pill>
        <select value={row.week_of} onChange={(e) => setSel(e.target.value)} style={selectStyle}>
          {rows.map((r) => <option key={r.week_of} value={r.week_of}>Week of {niceDate(r.week_of)}</option>)}
        </select></div>}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={card}>
          <div style={rowBetween}><h3 style={h3}>What was done</h3><span style={eyebrow}>date · where</span></div>
          {(w.log || []).length ? (w.log || []).map((l, i) => (
            <div key={i} style={{ padding: "9px 0", borderTop: i ? "1px solid #eef1ee" : "none", display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
              <div><div style={{ fontSize: 13.5, fontWeight: 600 }}>{l.what}</div><div className="jg-mono" style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{l.at}{safeUrl(l.link) && <> · <a href={l.link} target="_blank" rel="noreferrer" style={{ color: "#1f6e79" }}>Open</a></>}</div></div>
              <Pill tone="done">{l.where}</Pill>
            </div>)) : <p style={{ margin: "8px 0 0", color: "#6b7280", fontSize: 13 }}>Nothing posted yet. Say “start” in the Marketing Manager chat to post and upload.</p>}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(300px, 100%), 1fr))", gap: 14 }}>
          <div style={card}>
            <div style={rowBetween}><h3 style={h3}>Google post</h3><Pill tone={g.status === "posted" ? "done" : "ready"}>{g.status === "posted" ? "Posted" : "Draft"}</Pill></div>
            {g.article && <div style={{ fontSize: 13, margin: "10px 0" }}><span style={eyebrow}>From the blog</span><br /><a href={safeUrl(g.articleUrl)} target="_blank" rel="noreferrer" style={{ color: "#1f6e79" }}>{g.article}</a>{g.angle && <span style={{ color: "#6b7280" }}> · {g.angle}</span>}</div>}
            <div style={{ whiteSpace: "pre-wrap", background: "#eef1ee", borderRadius: 8, padding: 12, fontSize: 13.5, lineHeight: 1.5 }}>{g.post || "No post drafted."}</div>
            {g.button && <div style={{ fontSize: 12.5, color: "#6b7280", marginTop: 8 }}>Button: <b>{g.button}</b> → <a href={safeUrl(g.buttonUrl)} target="_blank" rel="noreferrer" style={{ color: "#1f6e79", wordBreak: "break-all" }}>{g.buttonUrl}</a></div>}
          </div>
          <div style={card}>
            <div style={rowBetween}><h3 style={h3}>Photos</h3><span style={eyebrow}>Google + Yelp</span></div>
            {(w.photos || []).map((p, i) => <div key={i} style={{ padding: "8px 0", borderTop: i ? "1px solid #eef1ee" : "none", fontSize: 13 }}><a href={safeUrl(p.url)} target="_blank" rel="noreferrer" style={{ color: "#1f6e79" }}>{p.name}</a></div>)}
          </div>
        </div>
        <div style={card}>
          <h3 style={h3}>Yelp</h3>
          {!(y.reviews || []).length && !(y.leads || []).length && <p style={{ margin: "8px 0 0", color: "#6b7280", fontSize: 13 }}>{y.note || "No new reviews or leads."}</p>}
          {(y.reviews || []).map((r, i) => (
            <div key={"r" + i} style={{ padding: "10px 0", borderTop: "1px solid #eef1ee" }}>
              <div style={rowBetween}><b style={{ fontSize: 13.5 }}>{r.reviewer}{r.rating ? " · " + "★".repeat(r.rating) : ""}</b><span style={{ display: "flex", gap: 6 }}>{r.flagged && <Pill tone="flag">Negative</Pill>}<Pill tone={r.status === "posted" ? "done" : "ready"}>{r.status === "posted" ? "Replied" : "Draft"}</Pill></span></div>
              {r.excerpt && <div style={{ fontSize: 12.5, color: "#6b7280", margin: "4px 0" }}>“{r.excerpt}”</div>}
              <div style={{ fontSize: 13, borderLeft: "3px solid #3ea4b1", paddingLeft: 10 }}>{r.reply}</div>
            </div>))}
          {(y.leads || []).map((l, i) => (
            <div key={"l" + i} style={{ padding: "10px 0", borderTop: "1px solid #eef1ee" }}>
              <div style={rowBetween}><b style={{ fontSize: 13.5 }}>Lead: {l.name}</b><Pill tone={l.status === "sent" ? "done" : "ready"}>{l.status === "sent" ? "Sent" : "Draft"}</Pill></div>
              <div style={{ fontSize: 12.5, color: "#6b7280", margin: "4px 0" }}>{l.summary}</div>
              <div style={{ fontSize: 13, borderLeft: "3px solid #3ea4b1", paddingLeft: 10 }}>{l.reply}</div>
            </div>))}
        </div>
        {(c.projects || c.funnel || c.issues) && <>
          <div style={{ ...rowBetween, marginTop: 6 }}><h2 className="jg-display" style={{ margin: 0, fontSize: 22, textTransform: "uppercase" }}>Clarity</h2>{c.range && <span style={eyebrow}>{c.range}</span>}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))", gap: 14 }}>
            {(c.projects || []).map((p, i) => (
              <div key={i} style={card}>
                <div style={rowBetween}><h3 style={h3}>{p.name}</h3>{p.site && <span style={eyebrow}>{p.site}</span>}</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 8, marginTop: 10 }}>
                  {(p.metrics || []).map((m, j) => (
                    <div key={j} style={{ background: "#eef1ee", borderRadius: 8, padding: "9px 11px" }}>
                      <div className="jg-mono" style={{ fontSize: 18, fontWeight: 500 }}>{fmtNum(m.value)}</div>
                      <div style={{ fontSize: 11.5, color: "#6b7280" }}>{m.label}</div>
                      <Delta cur={m.num} prev={m.prev} higherIsBetter={m.higherIsBetter !== false} />
                    </div>))}
                </div>
                {p.note && <p style={{ fontSize: 13, color: "#6b7280", margin: "10px 0 0" }}>{p.note}</p>}
              </div>))}
          </div>
          {(c.funnel || []).length > 0 && <div style={card}>
            <div style={rowBetween}><h3 style={h3}>Order flow funnel</h3><span style={eyebrow}>sessions reaching each step</span></div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
              {c.funnel.map((f, i, a) => {
                const drop = i > 0 && a[i - 1].sessions ? Math.round((1 - f.sessions / a[i - 1].sessions) * 100) : null;
                return <div key={i} style={{ display: "grid", gridTemplateColumns: "minmax(90px,190px) minmax(0,1fr) 52px", gap: 10, alignItems: "center", fontSize: 13 }}>
                  <span>{f.step}{drop != null && <span className="jg-mono" style={{ fontSize: 11, color: drop > 40 ? "#a53f1f" : "#6b7280" }}> −{drop}%</span>}</span>
                  <div style={{ height: 14, background: "#eef1ee", borderRadius: 3, overflow: "hidden" }}><div style={{ width: `${((f.sessions || 0) / fmax) * 100}%`, height: "100%", background: "#3ea4b1" }} /></div>
                  <span className="jg-mono" style={{ textAlign: "right" }}>{fmtNum(f.sessions)}</span>
                </div>;
              })}
            </div>
          </div>}
          {(c.issues || []).length > 0 && <div style={card}>
            <h3 style={h3}>Top issues</h3>
            {c.issues.map((it, i) => <div key={i} style={{ padding: "10px 0", borderTop: i ? "1px solid #eef1ee" : "none", fontSize: 13 }}>
              <b>{it.title}</b>{it.detail && <div style={{ color: "#6b7280", marginTop: 3 }}>{it.detail}</div>}{it.fix && <div style={{ borderLeft: "3px solid #3ea4b1", paddingLeft: 10, marginTop: 6 }}>Fix: {it.fix}</div>}
            </div>)}
          </div>}
        </>}
        {(a.metrics || a.channels || a.pages) && <>
          <div style={{ ...rowBetween, marginTop: 6 }}><h2 className="jg-display" style={{ margin: 0, fontSize: 22, textTransform: "uppercase" }}>Google Analytics</h2>{a.range && <span style={eyebrow}>{a.range}</span>}</div>
          {(a.metrics || []).length > 0 && <div style={card}>
            <div style={rowBetween}><h3 style={h3}>jg.graphics</h3><span style={eyebrow}>GA4 · last 7 days</span></div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 8, marginTop: 10 }}>
              {a.metrics.map((m, j) => (
                <div key={j} style={{ background: "#eef1ee", borderRadius: 8, padding: "9px 11px" }}>
                  <div className="jg-mono" style={{ fontSize: 18, fontWeight: 500 }}>{fmtNum(m.value)}</div>
                  <div style={{ fontSize: 11.5, color: "#6b7280" }}>{m.label}</div>
                  <Delta cur={m.num} prev={m.prev} higherIsBetter={m.higherIsBetter !== false} />
                </div>))}
            </div>
          </div>}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))", gap: 14 }}>
            {(a.channels || []).length > 0 && <div style={card}>
              <div style={rowBetween}><h3 style={h3}>Traffic sources</h3><span style={eyebrow}>sessions</span></div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                {a.channels.map((ch, i) => <div key={i} style={{ display: "grid", gridTemplateColumns: "minmax(90px,150px) minmax(0,1fr) 64px", gap: 10, alignItems: "center", fontSize: 13 }}>
                  <span>{ch.channel}</span>
                  <div style={{ height: 12, background: "#eef1ee", borderRadius: 3, overflow: "hidden" }}><div style={{ width: `${((ch.sessions || 0) / gmax) * 100}%`, height: "100%", background: "#3ea4b1" }} /></div>
                  <span className="jg-mono" style={{ textAlign: "right" }}>{fmtNum(ch.sessions)}<Delta cur={ch.sessions} prev={ch.prev} /></span>
                </div>)}
              </div>
            </div>}
            {(a.pages || []).length > 0 && <div style={card}>
              <div style={rowBetween}><h3 style={h3}>Top pages</h3><span style={eyebrow}>views</span></div>
              {a.pages.map((pg, i) => <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "8px 0", borderTop: i ? "1px solid #eef1ee" : "none", fontSize: 13 }}>
                <span style={{ minWidth: 0 }}>{pg.title}{pg.path && <div className="jg-mono" style={{ fontSize: 11, color: "#6b7280", wordBreak: "break-all" }}>{pg.path}</div>}</span>
                <span className="jg-mono" style={{ whiteSpace: "nowrap" }}>{fmtNum(pg.views)}</span>
              </div>)}
            </div>}
          </div>
          {((a.items || []).length > 0 || (a.devices || []).length > 0) && <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))", gap: 14 }}>
            {(a.items || []).length > 0 && <div style={card}>
              <div style={rowBetween}><h3 style={h3}>Top purchased items</h3><span style={eyebrow}>purchases · revenue</span></div>
              {a.items.map((it, i) => <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "8px 0", borderTop: i ? "1px solid #eef1ee" : "none", fontSize: 13 }}>
                <span>{it.name}</span><span className="jg-mono" style={{ whiteSpace: "nowrap" }}>{fmtNum(it.purchases)}{it.revenue != null && <> · {fmtNum(it.revenue)}</>}</span>
              </div>)}
            </div>}
            {(a.devices || []).length > 0 && <div style={card}>
              <h3 style={h3}>Devices</h3>
              {a.devices.map((d2, i) => <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderTop: i ? "1px solid #eef1ee" : "none", fontSize: 13 }}><span>{d2.device}</span><span className="jg-mono">{d2.pct}{typeof d2.pct === "number" ? "%" : ""}</span></div>)}
            </div>}
          </div>}
          {(a.takeaways || []).length > 0 && <div style={card}>
            <h3 style={h3}>Takeaways</h3>
            {a.takeaways.map((t2, i) => <div key={i} style={{ padding: "8px 0", borderTop: i ? "1px solid #eef1ee" : "none", fontSize: 13 }}>{t2}</div>)}
          </div>}
        </>}
      </div>
    </Header>
  );
}

// ================= SEO =================
function SeoTable({ rows, prevRows, keyField, firstCol }) {
  const pmap = new Map((prevRows || []).map((r) => [r[keyField], r]));
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 560 }}>
        <thead><tr>{[firstCol, "Clicks", "Impressions", "CTR", "Avg position"].map((h, i) => <th key={h} className="jg-mono" style={{ textAlign: i ? "right" : "left", fontSize: 10.5, color: "#6b7280", fontWeight: 500, padding: "8px 10px", borderBottom: "1px solid #dde2de", textTransform: "uppercase" }}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => {
            const p = pmap.get(r[keyField]) || {};
            const td = { padding: "8px 10px", borderBottom: "1px solid #eef1ee", textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" };
            return <tr key={i}>
              <td style={{ ...td, textAlign: "left", whiteSpace: "normal" }}>{safeUrl(r.url) ? <a href={r.url} target="_blank" rel="noreferrer" style={{ color: "#1f6e79" }}>{r[keyField]}</a> : r[keyField]}{r.note && <div style={{ fontSize: 11.5, color: "#6b7280" }}>{r.note}</div>}</td>
              <td style={td}>{fmtNum(r.clicks)}{prevRows && <Delta cur={r.clicks} prev={p.clicks} />}</td>
              <td style={td}>{fmtNum(r.impr)}{prevRows && <Delta cur={r.impr} prev={p.impr} />}</td>
              <td style={td}>{r.ctr == null ? "–" : r.ctr.toFixed(2) + "%"}{prevRows && <Delta cur={r.ctr} prev={p.ctr} unit="pt" />}</td>
              <td style={td}>{r.pos == null ? "–" : r.pos.toFixed(1)}{prevRows && <Delta cur={r.pos} prev={p.pos} unit="pos" />}</td>
            </tr>;
          })}
        </tbody>
      </table>
    </div>
  );
}
export function SeoReports() {
  const { rows, error } = useTable("seo_reports", "month");
  const [sel, setSel] = useState(null);
  if (rows === null) return <Header title="SEO" eyebrowText="GOOGLE SEARCH CONSOLE"><p style={{ color: "#6b7280" }}>Loading…</p></Header>;
  if (!rows.length) return <Header title="SEO" eyebrowText="GOOGLE SEARCH CONSOLE">{error && <div style={{ color: "#a53f1f", fontSize: 13 }}>{error}</div>}<div style={{ ...card, textAlign: "center", color: "#6b7280" }}>No SEO reports yet.</div></Header>;
  const idx = Math.max(0, rows.findIndex((r) => r.month === sel));
  const row = rows[idx]; const prev = rows[idx + 1];
  const d = row.data || {}; const pd = prev ? prev.data || {} : null;
  const t = d.totals || {}; const pt = (pd && pd.totals) || {};
  return (
    <Header title={d.label || row.month} eyebrowText="GOOGLE SEARCH CONSOLE · JG.GRAPHICS"
      right={<div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        {pd ? <Pill>vs previous month: {(pd.label || prev.month).replace(/ baseline$/i, "")}</Pill> : <Pill tone="teal">Starting point</Pill>}
        <select value={row.month} onChange={(e) => setSel(e.target.value)} style={selectStyle}>{rows.map((r) => <option key={r.month} value={r.month}>{(r.data && r.data.label) || r.month}</option>)}</select>
      </div>}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {d.summary && <p style={{ margin: 0, color: "#3a3d40", fontSize: 14, maxWidth: 760 }}>{d.summary}</p>}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 10 }}>
          <div style={{ ...card, padding: 14 }}><div className="jg-mono" style={{ fontSize: 22 }}>{fmtNum(t.clicks)}</div><div style={{ fontSize: 12, color: "#6b7280" }}>Clicks</div>{pd && <Delta cur={t.clicks} prev={pt.clicks} />}</div>
          <div style={{ ...card, padding: 14 }}><div className="jg-mono" style={{ fontSize: 22 }}>{fmtNum(t.impressions)}</div><div style={{ fontSize: 12, color: "#6b7280" }}>Impressions</div>{pd && <Delta cur={t.impressions} prev={pt.impressions} />}</div>
          {t.range && <div style={{ ...card, padding: 14 }}><div style={{ fontSize: 14, fontWeight: 600 }}>{t.range}</div><div style={{ fontSize: 12, color: "#6b7280" }}>Date range</div></div>}
        </div>
        <div style={card}><h3 style={{ ...h3, marginBottom: 8 }}>Key pages</h3><SeoTable rows={d.pages || []} prevRows={pd && pd.pages} keyField="label" firstCol="Page" /></div>
        <div style={card}><h3 style={{ ...h3, marginBottom: 8 }}>Key searches</h3><SeoTable rows={d.queries || []} prevRows={pd && pd.queries} keyField="q" firstCol="Search" /></div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(300px, 100%), 1fr))", gap: 14 }}>
          {(d.watch || []).length > 0 && <div style={card}><h3 style={h3}>What to watch</h3><ul style={{ margin: "10px 0 0", paddingLeft: 18, fontSize: 13.5, lineHeight: 1.55 }}>{d.watch.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
          {(d.recommendations || []).length > 0 && <div style={card}><h3 style={h3}>Recommended updates</h3><ul style={{ margin: "10px 0 0", paddingLeft: 18, fontSize: 13.5, lineHeight: 1.55 }}>{d.recommendations.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
        </div>
      </div>
    </Header>
  );
}

const rowBetween = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" };
const selectStyle = { padding: "7px 10px", border: "1px solid #dde2de", borderRadius: 8, background: "#fff", fontSize: 13 };
function Header({ title, eyebrowText, right, children }) {
  return (
    <>
      <div className="jg-mono" style={{ fontSize: 11, color: "#3ea4b1", letterSpacing: "0.08em", marginBottom: 4 }}>{eyebrowText}</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
        <h1 className="jg-display" style={{ margin: 0, fontSize: 30, color: "#14171a", textTransform: "uppercase" }}>{title}</h1>
        {right}
      </div>
      {children}
    </>
  );
}

export function HubIcon({ name }) {
  const common = { width: 20, height: 20, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8 };
  if (name === "calendar") return <svg {...common}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>;
  if (name === "report") return <svg {...common}><path d="M6 3h9l4 4v14H6z" /><path d="M9 12h7M9 16h7M9 8h3" /></svg>;
  return <svg {...common}><circle cx="11" cy="11" r="6" /><path d="M20 20l-4.5-4.5" /></svg>;
}

// ================= PLANNING (campaigns + idea list) =================
const ideaTypes = (v) => (v || "").split(",").map((x) => x.trim()).filter((x) => CAL_TYPES[x]);
export function PlanningView({ assignedChannels = [], isAdmin, userName, userId }) {
  const today = new Date();
  const todayIso = iso(today.getFullYear(), today.getMonth(), today.getDate());
  const me = userName || "Someone";
  const [campaigns, setCampaigns] = useState(null);
  const [ideas, setIdeas] = useState(null);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const [campForm, setCampForm] = useState(null);
  const [ideaForm, setIdeaForm] = useState(null);
  const [schedule, setSchedule] = useState(null); // {idea, type, date}
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // {table, id, name}
  const [votes, setVotes] = useState([]);
  const [comments, setComments] = useState([]);
  const [openThreads, setOpenThreads] = useState({});
  const [drafts, setDrafts] = useState({});
  const [posting, setPosting] = useState(null);

  const canSocial = isAdmin || assignedChannels.includes("instagram") || assignedChannels.includes("facebook");
  const allowed = { email: isAdmin || assignedChannels.includes("email"), post: canSocial, story: canSocial, tiktok: !!isAdmin, blog: isAdmin || assignedChannels.includes("blog") };
  const allowedTypes = TYPE_ORDER.filter((t) => allowed[t]);
  const mine = (r) => isAdmin || (userId && r.created_by === userId);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    Promise.all([
      supabase.from("campaigns").select("*").order("start_date", { ascending: true }),
      supabase.from("ideas").select("*").order("created_at", { ascending: false }),
    ]).then(([c, i]) => {
      if (cancelled) return;
      const err = c.error || i.error;
      if (err) setError(/relation|does not exist|schema cache/i.test(err.message) ? "Planning isn't set up in the database yet. Run supabase-planning.sql in Supabase." : err.message);
      const cutoff = addDays(todayIso, -365);
      const all = c.data || [];
      const expired = all.filter((x) => x.end_date < cutoff);
      setCampaigns(all.filter((x) => x.end_date >= cutoff)); setIdeas(i.data || []);
      // Campaigns that ended more than 12 months ago are deleted (with their comments)
      expired.filter((x) => isAdmin || (userId && x.created_by === userId)).forEach((x) => {
        supabase.from("campaigns").delete().eq("id", x.id).then(({ error }) => { if (!error) supabase.from("planning_comments").delete().eq("parent_id", x.id).then(() => {}); });
      });
    });
    supabase.from("idea_votes").select("idea_id,user_id,vote").then(({ data }) => { if (!cancelled) setVotes(data || []); });
    supabase.from("planning_comments").select("*").order("created_at", { ascending: true }).then(({ data }) => { if (!cancelled) setComments(data || []); });
    return () => { cancelled = true; };
  }, [reload, todayIso, isAdmin, userId]);

  async function saveCampaign() {
    const f = campForm;
    if (!f.name.trim() || !f.start_date || !f.end_date) { setError("A campaign needs a name, start date and end date."); return; }
    if (f.end_date < f.start_date) { setError("The end date is before the start date."); return; }
    setSaving(true); setError(null);
    const row = { name: f.name.trim(), start_date: f.start_date, end_date: f.end_date, goal: f.goal, notes: f.notes, platforms: f.platforms || {}, updated_at: new Date().toISOString() };
    const res = f.id ? await supabase.from("campaigns").update(row).eq("id", f.id) : await supabase.from("campaigns").insert({ ...row, created_by_name: me });
    setSaving(false);
    if (res.error) { setError(/platforms/i.test(res.error.message) ? "Campaign platforms aren't set up yet. Run supabase-campaign-platforms.sql in Supabase." : `Couldn't save: ${res.error.message}`); return; }
    setCampForm(null); setReload((n) => n + 1);
  }
  async function saveIdea() {
    const f = ideaForm;
    if (!f.title.trim()) { setError("Give the idea a title."); return; }
    setSaving(true); setError(null);
    const row = { title: f.title.trim(), idea_type: f.idea_type || null, notes: f.notes };
    const res = f.id ? await supabase.from("ideas").update(row).eq("id", f.id) : await supabase.from("ideas").insert({ ...row, created_by_name: me });
    setSaving(false);
    if (res.error) { setError(`Couldn't save: ${res.error.message}`); return; }
    setIdeaForm(null); setReload((n) => n + 1);
  }
  async function del(table, id) {
    setSaving(true);
    const { error } = await supabase.from(table).delete().eq("id", id);
    if (!error) supabase.from("planning_comments").delete().eq("parent_id", id).then(() => {});
    setSaving(false); setConfirmDel(null);
    if (error) setError(`Couldn't delete: ${error.message}`); else setReload((n) => n + 1);
  }
  const tally = (id) => {
    let up = 0, down = 0, my = 0;
    votes.forEach((v) => { if (v.idea_id !== id) return; if (v.vote > 0) up++; else down++; if (v.user_id === userId) my = v.vote; });
    return { up, down, my };
  };
  async function castVote(id, value) {
    if (!userId) return;
    const { my } = tally(id);
    const prev = votes;
    const others = votes.filter((v) => !(v.idea_id === id && v.user_id === userId));
    setVotes(my === value ? others : [...others, { idea_id: id, user_id: userId, vote: value }]);
    const res = my === value
      ? await supabase.from("idea_votes").delete().eq("idea_id", id).eq("user_id", userId)
      : await supabase.from("idea_votes").upsert({ idea_id: id, user_id: userId, vote: value }, { onConflict: "idea_id,user_id" });
    if (res.error) { setVotes(prev); setError(/relation|does not exist|schema cache/i.test(res.error.message) ? "Voting isn't set up yet. Run supabase-idea-votes.sql in Supabase." : `Couldn't save your vote: ${res.error.message}`); }
  }
  async function scheduleIdea() {
    const { idea, type, date } = schedule;
    if (!date) { setError("Pick a date."); return; }
    setSaving(true); setError(null);
    const now = new Date().toISOString();
    const channel = type === "email" ? "email" : type === "tiktok" ? "tiktok" : type === "blog" ? "blog" : (isAdmin || assignedChannels.includes("instagram")) ? "instagram" : "facebook";
    const ins = await supabase.from("content_items").insert({
      channel, item_date: date, title: idea.title, body: "", tag: type === "story" ? "Story" : type === "post" ? "Post" : null,
      type: CAL_TYPES[type].label, steps: (idea.notes || "").split("\n").map((s) => s.trim()).filter(Boolean),
      link: "", created_flag: false, done: false, source: "manual", updated_at: now, edited_by: me, edited_at: now,
    });
    if (ins.error) { setSaving(false); setError(`Couldn't schedule: ${ins.error.message}`); return; }
    if (mine(idea)) await supabase.from("ideas").delete().eq("id", idea.id);
    setSaving(false); setSchedule(null); setReload((n) => n + 1);
  }

  async function postComment(kind, id) {
    const key = kind + id;
    const body = (drafts[key] || "").trim();
    if (!body || !userId) return;
    setPosting(key); setError(null);
    const { data, error } = await supabase.from("planning_comments").insert({ parent_type: kind, parent_id: id, body, author_id: userId, author_name: me }).select().single();
    setPosting(null);
    if (error) { setError(/planning_comments|schema cache|does not exist/i.test(error.message) ? "Comments aren't set up yet. Run supabase-planning-comments.sql in Supabase." : `Couldn't post: ${error.message}`); return; }
    setComments((c) => [...c, data]); setDrafts((d) => ({ ...d, [key]: "" }));
  }
  async function deleteComment(cm) {
    const { error } = await supabase.from("planning_comments").delete().eq("id", cm.id);
    if (error) { setError(`Couldn't delete the comment: ${error.message}`); return; }
    setComments((c) => c.filter((x) => x.id !== cm.id));
  }
  const renderThread = (kind, id) => {
    const key = kind + id;
    const list = comments.filter((c) => c.parent_type === kind && c.parent_id === id);
    const open = !!openThreads[key];
    return (
      <div style={{ marginTop: 8 }}>
        <button onClick={() => setOpenThreads((o) => ({ ...o, [key]: !o[key] }))} aria-expanded={open} style={{ ...linkBtn, color: "#3a3d40", fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 5 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" /></svg>
          {list.length ? `${list.length} comment${list.length === 1 ? "" : "s"}` : "Comment"} <span style={{ fontSize: 10 }}>{open ? "▲" : "▼"}</span>
        </button>
        {open && (
          <div style={{ marginTop: 8, background: "#f6f8f7", borderRadius: 8, padding: "8px 10px" }}>
            {list.length === 0 && <div style={{ fontSize: 12.5, color: "#6b7280", padding: "2px 0 6px" }}>No comments yet.</div>}
            {list.map((cm) => (
              <div key={cm.id} style={{ padding: "6px 0", borderBottom: "1px solid #e6eae7" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{cm.author_name || "Someone"} <span className="jg-mono" style={{ fontWeight: 400, fontSize: 10.5, color: "#6b7280" }}>· {new Date(cm.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span></span>
                  {(isAdmin || cm.author_id === userId) && <button onClick={() => deleteComment(cm)} aria-label="Delete comment" style={{ background: "none", border: "none", color: "#a53f1f", fontSize: 11.5, padding: 0 }}>Delete</button>}
                </div>
                <div style={{ fontSize: 13, color: "#14171a", whiteSpace: "pre-wrap", marginTop: 2 }}>{cm.body}</div>
              </div>
            ))}
            <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
              <input aria-label="Write a comment" value={drafts[key] || ""} onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); postComment(kind, id); } }} placeholder="Write a comment…" style={{ ...inp, padding: "7px 9px" }} />
              <button onClick={() => postComment(kind, id)} disabled={posting === key || !(drafts[key] || "").trim()} style={{ ...primaryBtn, padding: "7px 12px", opacity: (drafts[key] || "").trim() ? 1 : 0.5 }}>{posting === key ? "…" : "Post"}</button>
            </div>
          </div>
        )}
      </div>
    );
  };
  const daysLeft = (end) => Math.round((new Date(end + "T12:00:00") - new Date(todayIso + "T12:00:00")) / 86400000);
  const range = (s, e) => `${new Date(s + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${new Date(e + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;

  return (
    <>
      <div className="jg-mono" style={{ fontSize: 11, color: "#3ea4b1", letterSpacing: "0.08em", marginBottom: 4 }}>PLANNING</div>
      <h1 className="jg-display" style={{ margin: "0 0 18px", fontSize: 30, color: "#14171a", textTransform: "uppercase" }}>Planning</h1>
      {error && <div style={{ ...card, color: "#a53f1f", marginBottom: 12, fontSize: 13 }}>{error}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(380px, 100%), 1fr))", gap: 16, alignItems: "start" }}>
        {/* Campaigns */}
        <div style={card}>
          <div style={rowBetween}>
            <div><h3 style={h3}>Campaigns</h3><div style={{ fontSize: 12, color: "#6b7280", marginTop: 3 }}>Soonest first. Finished campaigns move to the bottom and are deleted 12 months after they end.</div></div>
            <button onClick={() => { setError(null); setCampForm({ name: "", start_date: todayIso, end_date: todayIso, goal: "", notes: "", platforms: {} }); }} style={primaryBtn}>+ Campaign</button>
          </div>
          {campaigns === null && <p style={{ color: "#6b7280", fontSize: 13 }}>Loading…</p>}
          {campaigns && campaigns.filter((c) => c.end_date >= todayIso).length === 0 && <p style={{ color: "#6b7280", fontSize: 13, margin: "14px 0 0" }}>No current or upcoming campaigns. Add one, like “Holiday Gift Orders” or “Spring Team Season.”</p>}
          {[...(campaigns || []).filter((c) => c.end_date >= todayIso), ...(campaigns || []).filter((c) => c.end_date < todayIso).sort((x, y) => (x.end_date > y.end_date ? 1 : x.end_date < y.end_date ? -1 : (x.start_date > y.start_date ? 1 : -1)))].map((c, idx, arr) => {
            const ended = c.end_date < todayIso;
            const live = !ended && c.start_date <= todayIso;
            const left = daysLeft(c.end_date);
            const firstPast = ended && (idx === 0 || arr[idx - 1].end_date >= todayIso);
            return (
              <div key={c.id}>
              {firstPast && <div className="jg-mono" style={{ fontSize: 10.5, letterSpacing: "0.08em", color: "#9aa1a5", margin: "18px 0 0", paddingTop: 10, borderTop: "2px solid #e3e7e4" }}>PAST CAMPAIGNS</div>}
              <div style={{ padding: "12px 0", borderTop: firstPast ? "none" : "1px solid #eef1ee", marginTop: firstPast ? 0 : 10, filter: ended ? "grayscale(1)" : "none", opacity: ended ? 0.55 : 1 }}>
                <div style={rowBetween}>
                  <b style={{ fontSize: 14.5 }}>{c.name}</b>
                  <Pill tone={live ? "done" : "muted"}>{ended ? "Ended" : live ? (left === 0 ? "Ends today" : `Live · ${left} day${left === 1 ? "" : "s"} left`) : "Upcoming"}</Pill>
                </div>
                <div className="jg-mono" style={{ fontSize: 11.5, color: "#3a3d40", marginTop: 4 }}>{range(c.start_date, c.end_date)}</div>
                {c.goal && <div style={{ fontSize: 13, marginTop: 6 }}><span style={{ color: "#6b7280" }}>Goal:</span> {c.goal}</div>}
                {c.platforms && Object.keys(c.platforms).filter((t) => CAL_TYPES[t]).length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
                    {TYPE_ORDER.filter((t) => Object.prototype.hasOwnProperty.call(c.platforms, t)).map((t) => (
                      <div key={t} style={{ borderLeft: `3px solid ${CAL_TYPES[t].color}`, background: CAL_TYPES[t].color + "10", borderRadius: 5, padding: "5px 9px" }}>
                        <div style={{ fontSize: 11.5, fontWeight: 600 }}>{CAL_TYPES[t].label}</div>
                        {c.platforms[t] && <div style={{ fontSize: 12.5, color: "#3a3d40", whiteSpace: "pre-wrap", marginTop: 1 }}>{c.platforms[t]}</div>}
                      </div>
                    ))}
                  </div>
                )}
                {c.notes && <div style={{ fontSize: 13, color: "#3a3d40", marginTop: 6, whiteSpace: "pre-wrap" }}>{c.notes}</div>}
                <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
                  {c.created_by_name && <span className="jg-mono" style={{ fontSize: 10.5, color: "#6b7280" }}>Added by {c.created_by_name}</span>}
                  {mine(c) && <button onClick={() => setCampForm({ ...c, goal: c.goal || "", notes: c.notes || "", platforms: c.platforms || {} })} style={linkBtn}>Edit</button>}
                  {mine(c) && <button onClick={() => setConfirmDel({ table: "campaigns", id: c.id, name: c.name, noun: "campaign" })} style={delBtn}><TrashIcon /> Delete</button>}
                </div>
                {renderThread("campaign", c.id)}
              </div>
              </div>
            );
          })}
        </div>

        {/* Ideas */}
        <div style={card}>
          <div style={rowBetween}>
            <div><h3 style={h3}>Idea list</h3><div style={{ fontSize: 12, color: "#6b7280", marginTop: 3 }}>Drop ideas here, then schedule them onto the calendar.</div></div>
            <button onClick={() => { setError(null); setIdeaForm({ title: "", idea_type: "", notes: "" }); }} style={primaryBtn}>+ Idea</button>
          </div>
          {ideas === null && <p style={{ color: "#6b7280", fontSize: 13 }}>Loading…</p>}
          {ideas && ideas.length === 0 && <p style={{ color: "#6b7280", fontSize: 13, margin: "14px 0 0" }}>No ideas yet. Anyone on the team can add one.</p>}
          {(ideas || []).map((i) => (
            <div key={i.id} style={{ padding: "12px 0", borderTop: "1px solid #eef1ee", marginTop: 10 }}>
              <div style={rowBetween}>
                <b style={{ fontSize: 14 }}>{i.title}</b>
                {ideaTypes(i.idea_type).length > 0 && <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 6, justifyContent: "flex-end" }}>{ideaTypes(i.idea_type).map((t) => <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, padding: "2px 8px", borderRadius: 999, background: CAL_TYPES[t].color + "18" }}><span style={{ width: 8, height: 8, borderRadius: 2, background: CAL_TYPES[t].color }} />{CAL_TYPES[t].label}</span>)}</span>}
              </div>
              {i.notes && <div style={{ fontSize: 13, color: "#3a3d40", marginTop: 4, whiteSpace: "pre-wrap" }}>{i.notes}</div>}
              {(() => { const t = tally(i.id); return (
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button onClick={() => castVote(i.id, 1)} aria-pressed={t.my === 1} aria-label={`Thumbs up (${t.up})`} style={voteBtn(t.my === 1, "#1f6e3c", "#e2f3e8")}><ThumbIcon /> <span className="jg-mono">{t.up}</span></button>
                  <button onClick={() => castVote(i.id, -1)} aria-pressed={t.my === -1} aria-label={`Thumbs down (${t.down})`} style={voteBtn(t.my === -1, "#a53f1f", "#fbe6de")}><ThumbIcon down /> <span className="jg-mono">{t.down}</span></button>
                </div>
              ); })()}
              <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
                <span className="jg-mono" style={{ fontSize: 10.5, color: "#6b7280" }}>{i.created_by_name ? `${i.created_by_name} · ` : ""}{new Date(i.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                {allowedTypes.length > 0 && <button onClick={() => { setError(null); setSchedule({ idea: i, type: ideaTypes(i.idea_type).find((t) => allowed[t]) || allowedTypes[0], date: todayIso }); }} style={linkBtn}>Schedule →</button>}
                {mine(i) && <button onClick={() => setIdeaForm({ ...i, idea_type: i.idea_type || "", notes: i.notes || "" })} style={linkBtn}>Edit</button>}
                {mine(i) && <button onClick={() => setConfirmDel({ table: "ideas", id: i.id, name: i.title, noun: "idea" })} style={delBtn}><TrashIcon /> Delete</button>}
              </div>
              {renderThread("idea", i.id)}
            </div>
          ))}
        </div>
      </div>

      {confirmDel && (
        <div onClick={() => !saving && setConfirmDel(null)} style={{ position: "fixed", inset: 0, background: "rgba(20,23,26,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 60 }}>
          <div role="alertdialog" aria-labelledby="del-title" onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, padding: 22, width: "100%", maxWidth: 400, boxSizing: "border-box" }}>
            <h2 id="del-title" className="jg-display" style={{ margin: "0 0 8px", fontSize: 20, textTransform: "uppercase" }}>Delete this {confirmDel.noun}?</h2>
            <p style={{ margin: 0, fontSize: 14, color: "#3a3d40" }}><b>{confirmDel.name}</b> will be removed for everyone. This can't be undone.</p>
            <div style={{ display: "flex", gap: 8, marginTop: 18, justifyContent: "flex-end" }}>
              <button onClick={() => setConfirmDel(null)} disabled={saving} style={cancelBtn}>Cancel</button>
              <button onClick={() => del(confirmDel.table, confirmDel.id)} disabled={saving} style={{ background: "#a53f1f", color: "#fff", border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 13, fontWeight: 600 }}>{saving ? "Deleting…" : "Yes, delete"}</button>
            </div>
          </div>
        </div>
      )}

      {(campForm || ideaForm || schedule) && (
        <div onClick={() => !saving && (setCampForm(null), setIdeaForm(null), setSchedule(null))} style={{ position: "fixed", inset: 0, background: "rgba(20,23,26,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 50 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, padding: 24, width: "100%", maxWidth: 480, maxHeight: "92vh", overflowY: "auto", boxSizing: "border-box" }}>
            {campForm && <>
              <h2 className="jg-display" style={{ margin: "0 0 10px", fontSize: 22, textTransform: "uppercase" }}>{campForm.id ? "Edit campaign" : "New campaign"}</h2>
              <label style={lbl} htmlFor="cp-name">Campaign name</label>
              <input id="cp-name" value={campForm.name} onChange={(e) => setCampForm({ ...campForm, name: e.target.value })} placeholder="e.g. Holiday Gift Orders" style={inp} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div><label style={lbl} htmlFor="cp-start">Start</label><input id="cp-start" type="date" value={campForm.start_date} onChange={(e) => setCampForm({ ...campForm, start_date: e.target.value })} style={inp} /></div>
                <div><label style={lbl} htmlFor="cp-end">End</label><input id="cp-end" type="date" value={campForm.end_date} onChange={(e) => setCampForm({ ...campForm, end_date: e.target.value })} style={inp} /></div>
              </div>
              <label style={lbl} htmlFor="cp-goal">Goal <span style={{ color: "#9aa1a5" }}>(optional)</span></label>
              <input id="cp-goal" value={campForm.goal} onChange={(e) => setCampForm({ ...campForm, goal: e.target.value })} placeholder="e.g. 20 corporate gift orders by Dec 10" style={inp} />
              <label style={lbl}>Platforms <span style={{ color: "#9aa1a5" }}>(check each one this campaign uses)</span></label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {TYPE_ORDER.map((t) => {
                  const plats = campForm.platforms || {};
                  const on = Object.prototype.hasOwnProperty.call(plats, t);
                  const toggle = () => { const next = { ...plats }; if (on) delete next[t]; else next[t] = ""; setCampForm({ ...campForm, platforms: next }); };
                  return (
                    <div key={t} style={{ border: on ? `1.5px solid ${CAL_TYPES[t].color}` : "1px solid #dde2de", background: on ? CAL_TYPES[t].color + "0f" : "#fff", borderRadius: 8, padding: on ? "8px 10px 10px" : "8px 10px" }}>
                      <button type="button" role="checkbox" aria-checked={on} onClick={toggle} style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", padding: 0, fontSize: 13, fontWeight: on ? 600 : 400, color: "#14171a", width: "100%", textAlign: "left" }}>
                        <span style={{ width: 16, height: 16, borderRadius: 4, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", background: on ? CAL_TYPES[t].color : "#fff", border: on ? "none" : "1.5px solid #c7cdc9", color: "#fff", fontSize: 11 }}>{on ? "✓" : ""}</span>
                        {CAL_TYPES[t].label}
                      </button>
                      {on && <textarea aria-label={`${CAL_TYPES[t].label} notes`} rows={2} value={plats[t]} onChange={(e) => setCampForm({ ...campForm, platforms: { ...plats, [t]: e.target.value } })} placeholder={`What does ${CAL_TYPES[t].label} need for this campaign?`} style={{ ...inp, marginTop: 8, resize: "vertical" }} />}
                    </div>
                  );
                })}
              </div>
              <label style={lbl} htmlFor="cp-notes">Notes <span style={{ color: "#9aa1a5" }}>(optional)</span></label>
              <textarea id="cp-notes" rows={4} value={campForm.notes} onChange={(e) => setCampForm({ ...campForm, notes: e.target.value })} placeholder="Offer, who's involved, anything else…" style={{ ...inp, resize: "vertical" }} />
              <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
                <button onClick={saveCampaign} disabled={saving} style={primaryBtn}>{saving ? "Saving…" : campForm.id ? "Save changes" : "Add campaign"}</button>
                <button onClick={() => setCampForm(null)} style={cancelBtn}>Cancel</button>
              </div>
            </>}
            {ideaForm && <>
              <h2 className="jg-display" style={{ margin: "0 0 10px", fontSize: 22, textTransform: "uppercase" }}>{ideaForm.id ? "Edit idea" : "New idea"}</h2>
              <label style={lbl} htmlFor="id-title">Idea</label>
              <input id="id-title" value={ideaForm.title} onChange={(e) => setIdeaForm({ ...ideaForm, title: e.target.value })} placeholder="e.g. Behind-the-scenes press video" style={inp} />
              <label style={lbl}>Platforms <span style={{ color: "#9aa1a5" }}>(optional, pick any)</span></label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {TYPE_ORDER.map((t) => {
                  const sel = ideaTypes(ideaForm.idea_type);
                  const on = sel.includes(t);
                  const toggle = () => setIdeaForm({ ...ideaForm, idea_type: (on ? sel.filter((x) => x !== t) : [...sel, t]).join(",") });
                  return (
                    <button key={t} type="button" role="checkbox" aria-checked={on} onClick={toggle}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 11px", borderRadius: 8, fontSize: 13, textAlign: "left", border: on ? `2px solid ${CAL_TYPES[t].color}` : "1px solid #dde2de", background: on ? CAL_TYPES[t].color + "14" : "#fff", color: "#14171a" }}>
                      <span style={{ width: 16, height: 16, borderRadius: 4, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", background: on ? CAL_TYPES[t].color : "#fff", border: on ? "none" : "1.5px solid #c7cdc9", color: "#fff", fontSize: 11 }}>{on ? "✓" : ""}</span>
                      {CAL_TYPES[t].label}
                    </button>
                  );
                })}
              </div>
              <label style={lbl} htmlFor="id-notes">Notes <span style={{ color: "#9aa1a5" }}>(optional)</span></label>
              <textarea id="id-notes" rows={4} value={ideaForm.notes} onChange={(e) => setIdeaForm({ ...ideaForm, notes: e.target.value })} style={{ ...inp, resize: "vertical" }} />
              <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
                <button onClick={saveIdea} disabled={saving} style={primaryBtn}>{saving ? "Saving…" : ideaForm.id ? "Save changes" : "Add idea"}</button>
                <button onClick={() => setIdeaForm(null)} style={cancelBtn}>Cancel</button>
              </div>
            </>}
            {schedule && <>
              <h2 className="jg-display" style={{ margin: "0 0 6px", fontSize: 22, textTransform: "uppercase" }}>Schedule idea</h2>
              <p style={{ margin: "0 0 6px", fontSize: 14, fontWeight: 600 }}>{schedule.idea.title}</p>
              <label style={lbl}>Type</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {allowedTypes.map((t) => (
                  <button key={t} type="button" onClick={() => setSchedule({ ...schedule, type: t })}
                    style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 11px", borderRadius: 8, fontSize: 13, textAlign: "left", border: schedule.type === t ? `2px solid ${CAL_TYPES[t].color}` : "1px solid #dde2de", background: schedule.type === t ? CAL_TYPES[t].color + "14" : "#fff", color: "#14171a" }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: CAL_TYPES[t].color }} />{CAL_TYPES[t].label}
                  </button>
                ))}
              </div>
              <label style={lbl} htmlFor="sc-date">Date</label>
              <input id="sc-date" type="date" value={schedule.date} onChange={(e) => setSchedule({ ...schedule, date: e.target.value })} style={inp} />
              <p style={{ fontSize: 12, color: "#6b7280", margin: "10px 0 0" }}>It goes on the calendar as Planned{mine(schedule.idea) ? " and comes off the idea list" : ""}. Add the caption and details from the calendar.</p>
              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <button onClick={scheduleIdea} disabled={saving} style={primaryBtn}>{saving ? "Scheduling…" : "Add to calendar"}</button>
                <button onClick={() => setSchedule(null)} style={cancelBtn}>Cancel</button>
              </div>
            </>}
          </div>
        </div>
      )}
    </>
  );
}
const cancelBtn = { background: "#eef1ee", color: "#3a3d40", border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 13 };
const delBtn = { background: "none", border: "none", padding: 0, color: "#a53f1f", fontSize: 12.5, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 };
const voteBtn = (on, fg, bg) => ({ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 999, fontSize: 12.5, border: on ? `1.5px solid ${fg}` : "1px solid #dde2de", background: on ? bg : "#fff", color: on ? fg : "#3a3d40" });
function TrashIcon() { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>; }
function ThumbIcon({ down }) { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" style={down ? { transform: "rotate(180deg)" } : undefined}><path d="M7 11v9H4v-9zM7 11l4-8a2 2 0 0 1 2 2v4h5.5a2 2 0 0 1 2 2.3l-1.2 7A2 2 0 0 1 17.3 20H7" /></svg>; }

// ================= TEAM (admin only) =================
const CHANNEL_OPTS = [["email", "Email Marketing"], ["instagram", "Instagram"], ["facebook", "Facebook"], ["tiktok", "TikTok"], ["blog", "Blog"]];
export const TAB_OPTS = [["all", "Calendar"], ["planning", "Planning"], ["reports", "History"], ["seo", "SEO"], ["links", "Links"]];
const SYSTEM_EMAILS = ["claude@jg.graphics"];
function genPassword() {
  const a = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  const buf = new Uint32Array(14); (window.crypto || {}).getRandomValues ? window.crypto.getRandomValues(buf) : buf.forEach((_, i) => { buf[i] = Math.floor(Math.random() * 1e9); });
  return Array.from(buf, (n) => a[n % a.length]).join("");
}
function Check({ on, label, onClick, disabled }) {
  return (
    <button type="button" role="checkbox" aria-checked={on} disabled={disabled} onClick={onClick}
      style={{ display: "inline-flex", alignItems: "center", gap: 7, border: on ? "1.5px solid #3ea4b1" : "1px solid #dde2de", background: on ? "#e4f3f4" : "#fff", color: disabled ? "#9aa1a5" : "#14171a", borderRadius: 999, padding: "5px 11px 5px 7px", fontSize: 12.5, cursor: disabled ? "default" : "pointer" }}>
      <span style={{ width: 15, height: 15, borderRadius: 4, display: "inline-flex", alignItems: "center", justifyContent: "center", background: on ? "#3ea4b1" : "#fff", border: on ? "none" : "1.5px solid #c7cdc9", color: "#fff", fontSize: 10 }}>{on ? "✓" : ""}</span>{label}
    </button>
  );
}

export function TeamAdmin({ currentUserId, supabaseUrl, supabaseKey, createClientFn }) {
  const [people, setPeople] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(null);
  const [reload, setReload] = useState(0);
  const [adding, setAdding] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [pwFor, setPwFor] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      supabase.from("profiles").select("*").order("full_name"),
      supabase.from("profile_channels").select("*"),
    ]).then(([p, c]) => {
      if (cancelled) return;
      if (p.error || c.error) { setError((p.error || c.error).message); setPeople([]); return; }
      const ch = {}; (c.data || []).forEach((r) => { (ch[r.profile_id] = ch[r.profile_id] || []).push(r.channel); });
      const list = (p.data || []).map((x) => ({ ...x, channels: ch[x.id] || [] }));
      setPeople(list);
      const d = {}; list.forEach((x) => { d[x.id] = { full_name: x.full_name, email: x.email || "", role: x.role, channels: x.channels, tabs: x.tabs ? [...x.tabs] : TAB_OPTS.map((t) => t[0]) }; });
      setDrafts(d);
    });
    return () => { cancelled = true; };
  }, [reload]);

  const setD = (id, patch) => setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));
  const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const changed = (p) => { const d = drafts[p.id]; if (!d) return false; const tabs = p.tabs || TAB_OPTS.map((t) => t[0]); return d.full_name !== p.full_name || (d.email || "").trim().toLowerCase() !== (p.email || "").toLowerCase() || d.role !== p.role || [...d.channels].sort().join() !== [...p.channels].sort().join() || [...d.tabs].sort().join() !== [...tabs].sort().join(); };

  async function saveChannels(id, channels) {
    const del = await supabase.from("profile_channels").delete().eq("profile_id", id);
    if (del.error) return del.error;
    if (!channels.length) return null;
    const ins = await supabase.from("profile_channels").insert(channels.map((c) => ({ profile_id: id, channel: c })));
    return ins.error;
  }
  async function savePerson(p) {
    const d = drafts[p.id];
    if (!d.full_name.trim()) { setError("Name can't be empty."); return; }
    const newEmail = (d.email || "").trim().toLowerCase();
    const emailChanged = newEmail !== (p.email || "").toLowerCase();
    if (emailChanged && !/^\S+@\S+\.\S+$/.test(newEmail)) { setError("That email doesn't look right."); return; }
    setBusy(p.id); setError(null); setNotice(null);
    if (emailChanged) {
      const em = await supabase.rpc("admin_set_email", { uid: p.id, new_email: newEmail });
      if (em.error) { setBusy(null); setError(/function|schema cache/i.test(em.error.message) ? "Changing emails isn't set up yet. Run supabase-team-email.sql in Supabase." : `Couldn't change the email: ${em.error.message}`); return; }
    }
    const allTabs = TAB_OPTS.every((t) => d.tabs.includes(t[0]));
    const up = await supabase.from("profiles").update({ full_name: d.full_name.trim(), role: d.role, tabs: allTabs ? null : d.tabs }).eq("id", p.id);
    const chErr = up.error ? null : await saveChannels(p.id, d.channels);
    setBusy(null);
    const err = up.error || chErr;
    if (err) { setError(/tabs|email|column/i.test(err.message) ? "Team settings aren't set up yet. Run supabase-team-admin.sql in Supabase." : `Couldn't save: ${err.message}`); return; }
    setNotice(emailChanged ? `Saved ${d.full_name.trim()}. Their login email is now ${newEmail}; their password didn't change.` : `Saved ${d.full_name.trim()}. Changes apply the next time they load the dashboard.`);
    setReload((n) => n + 1);
  }
  async function addPerson() {
    const f = adding;
    if (!f.full_name.trim() || !/^\S+@\S+\.\S+$/.test(f.email) || f.password.length < 8) { setError("Add a name, a valid email and a password of at least 8 characters."); return; }
    setBusy("new"); setError(null); setNotice(null);
    const temp = createClientFn(supabaseUrl, supabaseKey, { auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false, storageKey: "jg-team-add" } });
    const { data, error } = await temp.auth.signUp({ email: f.email.trim().toLowerCase(), password: f.password });
    if (error || !data || !data.user) { setBusy(null); setError(`Couldn't create the login: ${error ? error.message : "unknown error"}. If sign-ups are turned off, enable Email sign-ups in Supabase → Authentication → Sign In / Providers.`); return; }
    const uid = data.user.id;
    if (data.user.identities && data.user.identities.length === 0) { setBusy(null); setError("That email already has a login. Find them in the list below, or use a different email."); return; }
    await supabase.rpc("admin_confirm_user", { uid });
    const allTabs = TAB_OPTS.every((t) => f.tabs.includes(t[0]));
    const ins = await supabase.from("profiles").insert({ id: uid, full_name: f.full_name.trim(), role: f.role, email: f.email.trim().toLowerCase(), tabs: allTabs ? null : f.tabs });
    const chErr = ins.error ? null : await saveChannels(uid, f.role === "admin" ? [] : f.channels);
    setBusy(null);
    if (ins.error || chErr) { setError(`Login created, but saving their access failed: ${(ins.error || chErr).message}`); setReload((n) => n + 1); return; }
    setNotice(`Added ${f.full_name.trim()}. Send them the email and password: ${f.email.trim().toLowerCase()} / ${f.password}`);
    setAdding(null); setReload((n) => n + 1);
  }
  async function deletePerson(p) {
    setBusy(p.id); setError(null); setNotice(null);
    const { error } = await supabase.rpc("admin_delete_user", { uid: p.id });
    setBusy(null); setConfirmDel(null);
    if (error) { setError(/function|schema cache/i.test(error.message) ? "Deleting users isn't set up yet. Run supabase-team-admin.sql in Supabase." : `Couldn't delete: ${error.message}`); return; }
    setNotice(`Deleted ${p.full_name}. Their login no longer works.`);
    setReload((n) => n + 1);
  }
  async function setPassword() {
    if (pwFor.password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setBusy(pwFor.id); setError(null);
    const { error } = await supabase.rpc("admin_set_password", { uid: pwFor.id, new_password: pwFor.password });
    setBusy(null);
    if (error) { setError(`Couldn't set the password: ${error.message}`); return; }
    setNotice(`New password for ${pwFor.name}: ${pwFor.password}`); setPwFor(null);
  }

  return (
    <>
      <div className="jg-mono" style={{ fontSize: 11, color: "#3ea4b1", letterSpacing: "0.08em", marginBottom: 4 }}>ADMIN</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
        <h1 className="jg-display" style={{ margin: 0, fontSize: 30, color: "#14171a", textTransform: "uppercase" }}>Team</h1>
        <button onClick={() => { setError(null); setAdding({ full_name: "", email: "", password: genPassword(), role: "rep", channels: [], tabs: TAB_OPTS.map((t) => t[0]) }); }} style={primaryBtn}>+ Add user</button>
      </div>
      {error && <div style={{ ...card, color: "#a53f1f", marginBottom: 12, fontSize: 13 }}>{error}</div>}
      {notice && <div style={{ ...card, color: "#1f6e3c", background: "#e2f3e8", borderColor: "#bfe3cb", marginBottom: 12, fontSize: 13, wordBreak: "break-word" }}>{notice}</div>}
      {people === null && <p style={{ color: "#6b7280" }}>Loading…</p>}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {(people || []).map((p) => {
          const d = drafts[p.id]; if (!d) return null;
          const isMe = p.id === currentUserId;
          const system = SYSTEM_EMAILS.includes((p.email || "").toLowerCase()) || /^Claude/.test(p.full_name);
          return (
            <div key={p.id} style={card}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end", justifyContent: "space-between" }}>
                <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                  <label style={lbl} htmlFor={`nm-${p.id}`}>Name {isMe && <span style={{ color: "#3ea4b1" }}>(you)</span>}{system && <span style={{ color: "#6b7280" }}> · used by the weekly runs</span>}</label>
                  <input id={`nm-${p.id}`} value={d.full_name} onChange={(e) => setD(p.id, { full_name: e.target.value })} style={inp} />
                  <label style={lbl} htmlFor={`em-${p.id}`}>Login email</label>
                  <input id={`em-${p.id}`} type="email" value={d.email} disabled={system} onChange={(e) => setD(p.id, { email: e.target.value })} placeholder="name@jg.graphics" style={{ ...inp, opacity: system ? 0.6 : 1 }} />
                </div>
                <div>
                  <label style={lbl}>Role</label>
                  <div style={{ display: "inline-flex", background: "#eef1ee", borderRadius: 8, padding: 3 }}>
                    {[["rep", "Rep"], ["admin", "Admin"]].map(([v, l]) => (
                      <button key={v} type="button" disabled={isMe || system} onClick={() => setD(p.id, { role: v })}
                        style={{ border: "none", borderRadius: 6, padding: "7px 16px", fontSize: 13, fontWeight: 600, background: d.role === v ? "#14171a" : "transparent", color: d.role === v ? "#fff" : "#3a3d40", opacity: (isMe || system) && d.role !== v ? 0.4 : 1 }}>{l}</button>
                    ))}
                  </div>
                </div>
              </div>
              <label style={lbl}>Can add and edit</label>
              {d.role === "admin" ? <div style={{ fontSize: 12.5, color: "#6b7280" }}>Admins can add and edit every platform, and see every tab including Team.</div> : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {CHANNEL_OPTS.map(([v, l]) => <Check key={v} on={d.channels.includes(v)} label={l} onClick={() => setD(p.id, { channels: toggle(d.channels, v) })} />)}
                </div>
              )}
              {d.role !== "admin" && <>
                <label style={lbl}>Tabs they can see</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {TAB_OPTS.map(([v, l]) => <Check key={v} on={d.tabs.includes(v)} label={l} onClick={() => setD(p.id, { tabs: toggle(d.tabs, v) })} />)}
                </div>
              </>}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", justifyContent: "space-between", marginTop: 14 }}>
                <button onClick={() => savePerson(p)} disabled={!changed(p) || busy === p.id} style={{ ...primaryBtn, opacity: changed(p) ? 1 : 0.4 }}>{busy === p.id ? "Saving…" : "Save changes"}</button>
                <div style={{ display: "flex", gap: 14 }}>
                  {!system && <button onClick={() => { setError(null); setPwFor({ id: p.id, name: p.full_name, password: genPassword() }); }} style={linkBtn}>Reset password</button>}
                  {!isMe && !system && <button onClick={() => setConfirmDel(p)} style={delBtn}><TrashIcon /> Delete user</button>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {(adding || confirmDel || pwFor) && (
        <div onClick={() => !busy && (setAdding(null), setConfirmDel(null), setPwFor(null))} style={{ position: "fixed", inset: 0, background: "rgba(20,23,26,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 60 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, padding: 22, width: "100%", maxWidth: confirmDel || pwFor ? 420 : 500, maxHeight: "92vh", overflowY: "auto", boxSizing: "border-box" }}>
            {confirmDel && <>
              <h2 className="jg-display" style={{ margin: "0 0 8px", fontSize: 20, textTransform: "uppercase" }}>Delete this user?</h2>
              <p style={{ margin: 0, fontSize: 14, color: "#3a3d40" }}><b>{confirmDel.full_name}</b> will lose their login right away. Content they added stays on the calendar. This can't be undone.</p>
              <div style={{ display: "flex", gap: 8, marginTop: 18, justifyContent: "flex-end" }}>
                <button onClick={() => setConfirmDel(null)} style={cancelBtn}>Cancel</button>
                <button onClick={() => deletePerson(confirmDel)} disabled={!!busy} style={{ background: "#a53f1f", color: "#fff", border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 13, fontWeight: 600 }}>{busy ? "Deleting…" : "Yes, delete"}</button>
              </div>
            </>}
            {pwFor && <>
              <h2 className="jg-display" style={{ margin: "0 0 8px", fontSize: 20, textTransform: "uppercase" }}>Reset password</h2>
              <p style={{ margin: "0 0 4px", fontSize: 13.5 }}>New password for <b>{pwFor.name}</b>:</p>
              <div style={{ display: "flex", gap: 8 }}>
                <input aria-label="New password" value={pwFor.password} onChange={(e) => setPwFor({ ...pwFor, password: e.target.value })} style={{ ...inp, fontFamily: "'IBM Plex Mono', monospace" }} />
                <button onClick={() => setPwFor({ ...pwFor, password: genPassword() })} style={cancelBtn}>New</button>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
                <button onClick={() => setPwFor(null)} style={cancelBtn}>Cancel</button>
                <button onClick={setPassword} disabled={!!busy} style={primaryBtn}>{busy ? "Saving…" : "Set password"}</button>
              </div>
            </>}
            {adding && <>
              <h2 className="jg-display" style={{ margin: "0 0 10px", fontSize: 22, textTransform: "uppercase" }}>Add user</h2>
              <label style={lbl} htmlFor="nu-name">Name</label>
              <input id="nu-name" value={adding.full_name} onChange={(e) => setAdding({ ...adding, full_name: e.target.value })} placeholder="First Last" style={inp} />
              <label style={lbl} htmlFor="nu-email">Email</label>
              <input id="nu-email" type="email" value={adding.email} onChange={(e) => setAdding({ ...adding, email: e.target.value })} placeholder="name@jg.graphics" style={inp} />
              <label style={lbl} htmlFor="nu-pw">Starting password</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input id="nu-pw" value={adding.password} onChange={(e) => setAdding({ ...adding, password: e.target.value })} style={{ ...inp, fontFamily: "'IBM Plex Mono', monospace" }} />
                <button onClick={() => setAdding({ ...adding, password: genPassword() })} style={cancelBtn}>New</button>
              </div>
              <label style={lbl}>Role</label>
              <div style={{ display: "inline-flex", background: "#eef1ee", borderRadius: 8, padding: 3 }}>
                {[["rep", "Rep"], ["admin", "Admin"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setAdding({ ...adding, role: v })} style={{ border: "none", borderRadius: 6, padding: "7px 16px", fontSize: 13, fontWeight: 600, background: adding.role === v ? "#14171a" : "transparent", color: adding.role === v ? "#fff" : "#3a3d40" }}>{l}</button>
                ))}
              </div>
              {adding.role === "rep" ? <>
                <label style={lbl}>Can add and edit</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>{CHANNEL_OPTS.map(([v, l]) => <Check key={v} on={adding.channels.includes(v)} label={l} onClick={() => setAdding({ ...adding, channels: toggle(adding.channels, v) })} />)}</div>
                <label style={lbl}>Tabs they can see</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {TAB_OPTS.map(([v, l]) => <Check key={v} on={adding.tabs.includes(v)} label={l} onClick={() => setAdding({ ...adding, tabs: toggle(adding.tabs, v) })} />)}
                </div>
              </> : <p style={{ fontSize: 12.5, color: "#6b7280", margin: "10px 0 0" }}>Admins can add and edit every platform and see every tab.</p>}
              <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
                <button onClick={addPerson} disabled={busy === "new"} style={primaryBtn}>{busy === "new" ? "Adding…" : "Add user"}</button>
                <button onClick={() => setAdding(null)} style={cancelBtn}>Cancel</button>
              </div>
            </>}
          </div>
        </div>
      )}
    </>
  );
}

// ================= MY SETTINGS (Slack notifications + calendar defaults) =================
const EVENT_OPTS = [
  ["added", "New content is added to the calendar", ""],
  ["my_changed", "Something I added is edited, moved or deleted", ""],
  ["posted", "Content is marked Posted", ""],
  ["campaign", "A new campaign is added", ""],
  ["idea", "A new idea is added", ""],
  ["comment", "Someone comments on a campaign or idea I added or commented on", ""],
];
const DEFAULT_SETTINGS = { events: { added: true, my_changed: true, posted: false, campaign: true, idea: false, comment: true }, platforms: ["email", "post", "story", "tiktok", "blog"], digest: "instant", cal_view: "week", cal_hidden: {} };

export function SettingsView({ userId, userName, initialNotice }) {
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(initialNotice || null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!userId) return;
    supabase.from("notification_settings").select("*").eq("user_id", userId).maybeSingle().then(({ data, error }) => {
      if (cancelled) return;
      if (error) { setError(/notification_settings|schema cache|does not exist/i.test(error.message) ? "Settings aren't set up yet. Run supabase-notifications.sql in Supabase." : error.message); }
      const row = { ...DEFAULT_SETTINGS, ...(data || {}), events: { ...DEFAULT_SETTINGS.events, ...((data && data.events) || {}) }, cal_view: (data && data.cal_view) || "week", cal_hidden: (data && data.cal_hidden) || {} };
      setS(row); setSaved(JSON.stringify(row));
    });
    return () => { cancelled = true; };
  }, [userId]);

  async function token() { const { data } = await supabase.auth.getSession(); return data.session ? data.session.access_token : ""; }
  async function connectSlack() {
    setBusy("connect"); setError(null);
    try {
      const r = await fetch("/.netlify/functions/slack-start", { method: "POST", headers: { Authorization: `Bearer ${await token()}` } });
      const j = await r.json();
      if (!r.ok || !j.url) throw new Error(j.error || "Couldn't start the Slack connection.");
      window.location.href = j.url;
    } catch (e) { setBusy(null); setError(e.message); }
  }
  async function testSlack() {
    setBusy("test"); setError(null); setNotice(null);
    try {
      const r = await fetch("/.netlify/functions/slack-test", { method: "POST", headers: { Authorization: `Bearer ${await token()}` } });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "The test didn't send.");
      setNotice("Test sent. Check your Slack messages from JG Marketing.");
    } catch (e) { setError(e.message); }
    setBusy(null);
  }
  async function disconnect() {
    setBusy("disconnect"); setError(null);
    const { error } = await supabase.from("notification_settings").update({ slack_user_id: null, slack_team_id: null, slack_name: null, updated_at: new Date().toISOString() }).eq("user_id", userId);
    setBusy(null);
    if (error) { setError(`Couldn't disconnect: ${error.message}`); return; }
    setS((x) => ({ ...x, slack_user_id: null, slack_name: null })); setNotice("Slack disconnected. You won't get alerts until you connect again.");
  }
  async function save() {
    setBusy("save"); setError(null); setNotice(null);
    const row = { user_id: userId, events: s.events, platforms: s.platforms, digest: s.digest, cal_view: s.cal_view, cal_hidden: s.cal_hidden, updated_at: new Date().toISOString() };
    const { error } = await supabase.from("notification_settings").upsert(row, { onConflict: "user_id" });
    setBusy(null);
    if (error) { setError(/notification_settings|schema cache/i.test(error.message) ? "Settings aren't set up yet. Run supabase-notifications.sql in Supabase." : `Couldn't save: ${error.message}`); return; }
    setSaved(JSON.stringify(s)); setNotice("Settings saved.");
    try { localStorage.setItem("jg-cal-hidden", JSON.stringify(s.cal_hidden || {})); } catch (e) {}
  }
  const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const dirty = s && saved !== JSON.stringify(s);

  return (
    <>
      <div className="jg-mono" style={{ fontSize: 11, color: "#3ea4b1", letterSpacing: "0.08em", marginBottom: 4 }}>MY SETTINGS</div>
      <h1 className="jg-display" style={{ margin: "0 0 4px", fontSize: 30, color: "#14171a", textTransform: "uppercase" }}>Settings</h1>
      <p style={{ margin: "0 0 18px", fontSize: 13.5, color: "#6b7280" }}>{userName ? `${userName} · ` : ""}These only change things for you.</p>
      {error && <div style={{ ...card, color: "#a53f1f", marginBottom: 12, fontSize: 13 }}>{error}</div>}
      {notice && <div style={{ ...card, color: "#1f6e3c", background: "#e2f3e8", borderColor: "#bfe3cb", marginBottom: 12, fontSize: 13 }}>{notice}</div>}
      {!s && <p style={{ color: "#6b7280" }}>Loading…</p>}
      {s && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(420px, 100%), 1fr))", gap: 16, alignItems: "start" }}>
          <div style={card}>
            <h3 style={h3}>Notifications</h3>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 12, padding: 12, borderRadius: 10, background: s.slack_user_id ? "#e2f3e8" : "#f6f8f7", border: "1px solid #dde2de" }}>
              <svg width="28" height="28" viewBox="0 0 24 24" aria-hidden="true"><path fill="#E01E5A" d="M5 15a2 2 0 1 1-2-2h2v2zm1 0a2 2 0 1 1 4 0v5a2 2 0 1 1-4 0v-5z"/><path fill="#36C5F0" d="M9 5a2 2 0 1 1 2-2v2H9zm0 1a2 2 0 1 1 0 4H4a2 2 0 1 1 0-4h5z"/><path fill="#2EB67D" d="M19 9a2 2 0 1 1 2 2h-2V9zm-1 0a2 2 0 1 1-4 0V4a2 2 0 1 1 4 0v5z"/><path fill="#ECB22E" d="M15 19a2 2 0 1 1-2 2v-2h2zm0-1a2 2 0 1 1 0-4h5a2 2 0 1 1 0 4h-5z"/></svg>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{s.slack_user_id ? "Slack connected" : "Slack"}</div>
                <div style={{ fontSize: 12.5, color: "#3a3d40" }}>{s.slack_user_id ? `Alerts go to ${s.slack_name || "you"} as direct messages from JG Marketing.` : "Connect your Slack to get calendar alerts as direct messages."}</div>
              </div>
              {s.slack_user_id ? (
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <button onClick={testSlack} disabled={!!busy} style={linkBtn}>{busy === "test" ? "Sending…" : "Send test"}</button>
                  <button onClick={disconnect} disabled={!!busy} style={{ ...linkBtn, color: "#a53f1f" }}>{busy === "disconnect" ? "…" : "Disconnect"}</button>
                </div>
              ) : (
                <button onClick={connectSlack} disabled={!!busy} style={{ ...primaryBtn, background: "#4A154B" }}>{busy === "connect" ? "Opening Slack…" : "Connect Slack"}</button>
              )}
            </div>

            <label style={{ ...lbl, marginTop: 16 }}>Notify me when</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {EVENT_OPTS.map(([k, label, hint]) => (
                <label key={k} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13.5, cursor: "pointer" }}>
                  <input type="checkbox" checked={!!s.events[k]} onChange={(e) => setS({ ...s, events: { ...s.events, [k]: e.target.checked } })} style={{ width: 16, height: 16, marginTop: 2, accentColor: "#3ea4b1" }} />
                  <span>{label}{hint && <span style={{ display: "block", fontSize: 11.5, color: "#6b7280" }}>{hint}</span>}</span>
                </label>
              )).reduce((acc, el, idx) => {
                acc.push(el);
                const k = EVENT_OPTS[idx][0];
                if ((k === "added" || k === "posted") && s.events[k]) {
                  const list = k === "added" ? s.platforms : (s.events.posted_types || s.platforms);
                  const setList = (next) => (k === "added" ? setS({ ...s, platforms: next }) : setS({ ...s, events: { ...s.events, posted_types: next } }));
                  acc.push(
                    <div key={k + "-types"} style={{ marginLeft: 26, marginTop: -2, marginBottom: 4 }}>
                      <div style={{ fontSize: 11.5, color: "#6b7280", marginBottom: 5 }}>Only for these types:</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {TYPE_ORDER.map((t) => <Check key={t} on={list.includes(t)} label={CAL_TYPES[t].label} onClick={() => setList(toggle(list, t))} />)}
                      </div>
                      {list.length === 0 && <div style={{ fontSize: 11.5, color: "#a53f1f", marginTop: 4 }}>Pick at least one type, or uncheck this alert.</div>}
                    </div>
                  );
                }
                return acc;
              }, [])}
            </div>
            <label style={lbl}>How often</label>
            <div style={{ display: "inline-flex", background: "#eef1ee", borderRadius: 8, padding: 3 }}>
              {[["instant", "Right away"], ["daily", "Daily summary (8am)"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setS({ ...s, digest: v })} style={{ border: "none", borderRadius: 6, padding: "7px 14px", fontSize: 13, fontWeight: 600, background: s.digest === v ? "#14171a" : "transparent", color: s.digest === v ? "#fff" : "#3a3d40" }}>{l}</button>
              ))}
            </div>
            {!s.slack_user_id && <p style={{ fontSize: 12, color: "#6b7280", margin: "10px 0 0" }}>These take effect once Slack is connected.</p>}
          </div>

          <div style={card}>
            <h3 style={h3}>Calendar</h3>
            <label style={lbl}>Open the calendar on</label>
            <div style={{ display: "inline-flex", background: "#eef1ee", borderRadius: 8, padding: 3 }}>
              {[["day", "Daily"], ["week", "Weekly"], ["month", "Monthly"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setS({ ...s, cal_view: v })} style={{ border: "none", borderRadius: 6, padding: "7px 14px", fontSize: 13, fontWeight: 600, background: s.cal_view === v ? "#14171a" : "transparent", color: s.cal_view === v ? "#fff" : "#3a3d40" }}>{l}</button>
              ))}
            </div>
            <label style={lbl}>Show these platforms</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {TYPE_ORDER.map((t) => <Check key={t} on={!s.cal_hidden[t]} label={CAL_TYPES[t].label} onClick={() => setS({ ...s, cal_hidden: { ...s.cal_hidden, [t]: !s.cal_hidden[t] } })} />)}
            </div>
            <p style={{ fontSize: 12, color: "#6b7280", margin: "10px 0 0" }}>You can still switch views and platforms on the calendar anytime. This is what it opens with.</p>
          </div>
        </div>
      )}
      {s && (
        <div style={{ position: "sticky", bottom: 0, padding: "14px 0", marginTop: 8, background: "linear-gradient(transparent, #eef1ee 30%)", display: "flex", gap: 10, alignItems: "center" }}>
          <button onClick={save} disabled={!dirty || busy === "save"} style={{ ...primaryBtn, opacity: dirty ? 1 : 0.45 }}>{busy === "save" ? "Saving…" : "Save settings"}</button>
          {dirty && <span style={{ fontSize: 12.5, color: "#6b7280" }}>Unsaved changes</span>}
        </div>
      )}
    </>
  );
}
